/* =========================================================
   BLOC 1 - IMPORTS
========================================================= */
import { createSupabaseClient } from "./supabaseDynamic";

import { lazy, Suspense, useState, useEffect } from "react";
import { sauvegarderMagasinsClub } from "./magasinsClub";
import logo from "./logo.jpg";
import { supabase, supabaseBootstrap } from "./supabaseClient";
import ClientsInterclubAValider from "./ClientsInterclubAValider";
import AdministrationGroupes from "./AdministrationGroupes";
import QuestionnaireDemandeGroupe from "./QuestionnaireDemandeGroupe";
import QuestionnaireDemandeUtilisateurs from "./QuestionnaireDemandeUtilisateurs";
import ActivationUtilisateur from "./ActivationUtilisateur";
import DevisCRM from "./DevisCRM";
import PageDevisClient from "./PageDevisClient";
import DashboardDevis from "./DashboardDevis";
import ContratsCRM from "./ContratsCRM";
import PageContratClient from "./PageContratClient";
import PageBatClient from "./PageBatClient";
import DashboardContrats from "./DashboardContrats";
import DashboardCrm from "./DashboardCrm";
import PlanningCRM from "./PlanningCRM";
import { accesGestionPrimes } from "./gestionPrimesAccess";
import { encoderCommentaireAction, estVisiteRealisee, normaliserTypeAction } from "./planningCrmUtils";
import { RendezVousPublic, creerDemandeRdv } from "./RendezVousCRM";
import { annulerDemandeRdv } from "./rendezVousApi";
import './crmClubPage.css';
import CycleCommercialInterclub from "./CycleCommercialInterclub";
import PortailClub from "./PortailClub";
import BoutiqueClubPublic from "./BoutiqueClubPublic";
import BoutiqueClubRetraitVendeur from "./BoutiqueClubRetraitVendeur";
import { BOUTIQUECLUB_ENABLED } from "./featureFlags";
import { documentsPourCodeClient, getSituationInterclub } from "./interclubCycle";
import { mouvementsPaiementInterclub } from "./interclubDirectSync";
import { syntheseFinancePointCommercial } from "./pointCommercialUtils";
import { syntheseTransformationDevis } from "./dashboardCrmUtils";
import { appliquerAttributionsCommercialesAuditees } from "./attributionCommercialeLocale";
import ObjectifsCommercial from "./ObjectifsCommercial";
import { estHotePortailBoutiqueClub, resoudreRouteBoutiqueClub } from "./boutiqueClubRouting";
import { lireFiltreDashboard, memoriserFiltresDashboard, oublierFiltresDashboard } from "./dashboardFilterSession";
import { creerDashboardFilterContext } from "./dashboardFilterContext";
import { creerContexteFinancier } from "./financialContext";
const VERSION = "v8.3.70";

// Les écrans d'administration et d'export Excel sont chargés uniquement à
// l'ouverture. Le portail public et le tableau de bord restent dans le bundle
// initial afin de préserver leur temps d'affichage et leur comportement.
const ImportFichiers = lazy(() => import("./ImportFichiers"));
const SyntheseInterclub = lazy(() => import("./SyntheseInterclub"));
const KilometrageCRM = lazy(() => import("./KilometrageLibreCRM"));
const GestionPrimes = lazy(() => import("./GestionPrimes"));
const BoutiqueClub = lazy(() => import("./BoutiqueClub"));
const AnnuaireClubs = lazy(() => import("./AnnuaireClubs"));

export async function modifierContactCRM(clientSupabase, clubId, contact) {
  if (!clientSupabase || !clubId || !contact?.id) throw new Error('Contact ou Club introuvable.');
  const payload = {
    nom:String(contact.nom || '').trim(), fonction:String(contact.fonction || '').trim(),
    telephone:String(contact.telephone || '').trim(), mail:String(contact.mail || '').trim()
  };
  if (!payload.nom) throw new Error('Le nom du contact est obligatoire.');
  const { data, error } = await clientSupabase.from('contacts').update(payload)
    .eq('id',contact.id).eq('club_id',clubId).select('*').single();
  if (error || !data) throw error || new Error('Contact introuvable.');
  return data;
}

function getSemaineIso(dateSource = new Date()) {
  const date = new Date(
    dateSource.getFullYear(),
    dateSource.getMonth(),
    dateSource.getDate()
  );
  const jourIso = date.getDay() || 7;
  date.setDate(date.getDate() + 4 - jourIso);
  const premierJanvier = new Date(date.getFullYear(), 0, 1);
  return {
    annee: date.getFullYear(),
    semaine: Math.ceil(((date - premierJanvier) / 86400000 + 1) / 7)
  };
}

function getLundiSemaineIso(annee, semaine) {
  const quatreJanvier = new Date(Number(annee), 0, 4);
  const jourIso = quatreJanvier.getDay() || 7;
  const lundi = new Date(quatreJanvier);
  lundi.setDate(quatreJanvier.getDate() - jourIso + 1 + (Number(semaine) - 1) * 7);
  lundi.setHours(0, 0, 0, 0);
  return lundi;
}

function getNombreSemainesIso(annee) {
  return getSemaineIso(new Date(Number(annee), 11, 28)).semaine;
}

function formatDateCourte(date) {
  return date.toLocaleDateString("fr-FR");
}
// FALLBACK TEMPORAIRE ÉTAPE 35 : passer à false pour réactiver l'ancien
// parcours username -> utilisateurs_profils -> email côté frontend.
const AUTH_USERNAME_EDGE_ACTIVE = process.env.REACT_APP_AUTH_USERNAME_EDGE_ACTIVE !== "false";

// Supabase conserve son verrou Auth pendant l'exécution du callback.
// Toute requête utilisant le même client doit donc démarrer au tour suivant.
export const planifierApresVerrouAuth = (traitement) => setTimeout(traitement, 0);
export const DELAI_CONTROLE_COMPTE_ACTIF_MS = 5 * 60 * 1000;

export function estRecetteLocaleDemandee(recherche = "", hostname = "") {
  const parametres = new URLSearchParams(recherche);
  return parametres.has("recette-groupes")
    && ["localhost", "127.0.0.1"].includes(String(hostname || "").toLowerCase());
}

export function resoudreGroupeInitial(recherche = "", hostname = "") {
  const parametres = new URLSearchParams(recherche);
  const groupeUrl = parametres.get("groupe");
  return String(groupeUrl || (estRecetteLocaleDemandee(recherche, hostname) ? "RECETTE" : "FORQUET")).trim().toUpperCase();
}

export function canoniserLienRecetteLocale(href = "") {
  const url = new URL(href);
  if (!estRecetteLocaleDemandee(url.search, url.hostname) || url.searchParams.has("groupe")) return url.toString();
  url.searchParams.set("groupe", "RECETTE");
  return url.toString();
}

export function estCibleSupabaseRecetteLocale(urlSource = "") {
  try {
    const url = new URL(urlSource);
    return ["localhost", "127.0.0.1"].includes(url.hostname) && url.port === "54321";
  } catch (_) {
    return false;
  }
}

function estCallbackPasswordRecovery() {
  const recherche = new URLSearchParams(window.location.search);
  const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ""));

  return (
    fragment.get("type") === "recovery" ||
    fragment.has("error_code") ||
    recherche.has("code") ||
    recherche.has("error_code")
  );
}

export const DELAI_CHARGEMENT_INITIAL_MS = 8000;

export async function avecDelaiMaximum(promise, delaiMs = DELAI_CHARGEMENT_INITIAL_MS) {
  let minuterie;
  const erreurDelai = new Promise((_, rejeter) => {
    minuterie = setTimeout(() => {
      const erreur = new Error("Le service de données ne répond pas dans le délai attendu.");
      erreur.code = "INITIALISATION_TIMEOUT";
      rejeter(erreur);
    }, delaiMs);
  });

  try {
    return await Promise.race([Promise.resolve(promise), erreurDelai]);
  } finally {
    clearTimeout(minuterie);
  }
}
/* =========================================================
   BLOC 2 - DÉBUT COMPOSANT + UTILISATEURS
========================================================= */
function AppContenu() {

  const demandeGroupeToken = new URLSearchParams(window.location.search).get("demande-groupe");
  const demandeUtilisateursToken = new URLSearchParams(window.location.search).get("demande-utilisateurs");
  const activationUtilisateurToken = new URLSearchParams(window.location.search).get("activation-utilisateur");
  const devisPublicToken = new URLSearchParams(window.location.search).get("devis-public");
  const contratPublicToken = new URLSearchParams(window.location.search).get("contrat-public");
  const batPublicToken = new URLSearchParams(window.location.search).get("bat-public");
  const rendezVousPublicToken = new URLSearchParams(window.location.search).get("rdv");
  const portailClubCampagneId = BOUTIQUECLUB_ENABLED ? new URLSearchParams(window.location.search).get("portail_club") : null;
  const rechercheUrl = new URLSearchParams(window.location.search);
  const routeBoutiqueClub = BOUTIQUECLUB_ENABLED ? resoudreRouteBoutiqueClub(window.location.pathname) : { type: "INTROUVABLE" };
  const routeBoutiqueClubActive = BOUTIQUECLUB_ENABLED && (estHotePortailBoutiqueClub(window.location.hostname) || (window.location.pathname !== "/" && routeBoutiqueClub.type !== "INTROUVABLE"));
  const boutiqueClubPublicActif = BOUTIQUECLUB_ENABLED && (rechercheUrl.has("boutiqueclub") || routeBoutiqueClubActive);
  const boutiqueClubPublicGroupe = rechercheUrl.has("boutiqueclub") ? rechercheUrl.get("boutiqueclub") : routeBoutiqueClub.groupeSlug;
  const boutiqueClubPublicCampagne = rechercheUrl.get("campagne") || routeBoutiqueClub.campagneId || null;
  const boutiqueClubPublicPack = rechercheUrl.get("pack") || routeBoutiqueClub.packId || null;
  const boutiqueClubPublicDocument = rechercheUrl.get("document") || null;
  const enlevementBoutiqueClub = BOUTIQUECLUB_ENABLED && new URLSearchParams(window.location.search).has("enlevement-boutiqueclub");
  // L'ancienne URL d'enlèvement n'est plus un parcours public : elle doit
  // attendre la session et ouvrir le workflow actuel avec les droits du rôle.
  const parcoursPublic = demandeGroupeToken || demandeUtilisateursToken || activationUtilisateurToken || devisPublicToken || contratPublicToken || batPublicToken || rendezVousPublicToken || portailClubCampagneId || boutiqueClubPublicActif;
  const [groupeActif, setGroupeActif] = useState(null);
  const [groupeErreur, setGroupeErreur] = useState("");
  const [reessaiInitialisation, setReessaiInitialisation] = useState(0);
const [supabaseClientActif, setSupabaseClientActif] = useState(null);
    const [groupeSelectionne, setGroupeSelectionne] = useState(() => {
      return resoudreGroupeInitial(window.location.search, window.location.hostname);
    });
useEffect(() => {
  const urlCanonique = canoniserLienRecetteLocale(window.location.href);
  if (urlCanonique !== window.location.href) window.history.replaceState({}, document.title, urlCanonique);
}, []);
useEffect(() => {
  if (!groupeActif) return;

const projectId = groupeActif.id_projet || groupeActif.project_id;
const apiKey = groupeActif.cle_base_superieure || groupeActif.supabase_key;

if (!projectId || !apiKey) {
  alert("CONFIG GROUPE INVALIDE");
  return;
}

const client = createSupabaseClient(
  projectId,
  apiKey
);

  setSupabaseClientActif(client);

  console.log("CLIENT GROUPE PRÊT");
}, [groupeActif]);
  useEffect(() => {
    let actif = true;
    async function chargerGroupe() {
      if (parcoursPublic) return;
      setGroupeErreur("");
      if (groupeSelectionne === "RECETTE" && !estCibleSupabaseRecetteLocale(supabaseBootstrap.supabaseUrl)) {
        const message = `RECETTE locale refusée : la base configurée n'est pas locale (${supabaseBootstrap.supabaseUrl || "URL absente"}).`;
        console.error(message);
        setGroupeErreur(message);
        return;
      }
      let data;
      let error;
      try {
        const resultat = await avecDelaiMaximum(
          supabaseBootstrap
            .from("groupes")
            .select("code,id_projet,cle_base_superieure")
            .eq("code", groupeSelectionne)
            .single()
        );
        data = resultat.data;
        error = resultat.error;
      } catch (erreur) {
        if (!actif) return;
        const message = erreur?.code === "INITIALISATION_TIMEOUT"
          ? "Le service CRM ne répond pas dans le délai attendu. Votre session est conservée : réessayez dans quelques instants."
          : `Impossible d'initialiser le CRM : ${erreur?.message || "erreur inconnue"}`;
        console.error("ERREUR INITIALISATION GROUPE :", erreur);
        setGroupeErreur(message);
        return;
      }

      if (!actif) return;

      if (error) {
        const message = "ERREUR GROUPE : " +
          (error.message || "") +
          " | code=" +
          (error.code || "") +
          " | details=" +
          (error.details || "");
        console.error(message);
        setGroupeErreur(message);
        return;
      }

      setGroupeErreur("");
      setGroupeActif(data);
    }

    chargerGroupe();
    return () => { actif = false; };
 }, [groupeSelectionne, parcoursPublic, reessaiInitialisation]);
 
  // 👉 LOG propre (en dehors du chargement)
useEffect(() => {
  if (groupeActif) {
    console.log("GROUPE ACTIF :", groupeActif.code);
  }
}, [groupeActif]);
/* =========================================================
   BLOC 2A - PROFIL UTILISATEUR SUPABASE
========================================================= */
async function chargerProfilUtilisateurParId(userId) {
  if (!userId) return null;
  if (!supabaseClientActif) return null;

  const { data, error } = await supabaseClientActif
    .from("utilisateurs_profils")
    .select("*")
    .eq("groupe", groupeSelectionne)
    .eq("id", userId)
    .eq("actif", true)
    .single();

  if (error) {
    console.log("ERREUR FETCH PROFIL ID :", error);
    return null;
  }

  return data || null;
}

// FALLBACK TEMPORAIRE ÉTAPE 35 : conservé, mais inutilisé dans le parcours normal.
async function chargerProfilUtilisateurParUsername(usernameSaisi) {
	if (!supabaseClientActif) {
  console.log("CLIENT SUPABASE PAS ENCORE PRET");
  return null;
}
  const usernameNettoye = String(usernameSaisi || "").trim().toLowerCase();

  if (!usernameNettoye) return null;

  const { data, error } = await supabaseClientActif
    .from("utilisateurs_profils")
    .select("*")
    .eq("groupe", groupeSelectionne)
    .eq("username", usernameNettoye)
    .eq("actif", true)
    .single();

  if (error) {
    console.log("ERREUR FETCH PROFIL USERNAME :", error);
    return null;
  }

  return data || null;
}
 /* =========================================================
   BLOC 3 - FONCTIONS OUTILS DATE ET PLANNING
========================================================= */ 
function getDateJourPlanning(jour) {
  const jours = {
    Lundi: 1,
    Mardi: 2,
    Mercredi: 3,
    Jeudi: 4,
    Vendredi: 5,
    Samedi: 6
  };

  const aujourdHui = new Date();
  const copie = new Date(aujourdHui);
  const jourActuel = copie.getDay();
  const jourCible = jours[jour] || 1;

  const diff = jourActuel === 0
    ? jourCible - 7
    : jourCible - jourActuel;

  copie.setDate(copie.getDate() + diff);

  const annee = copie.getFullYear();
  const mois = String(copie.getMonth() + 1).padStart(2, "0");
  const jourDuMois = String(copie.getDate()).padStart(2, "0");

  return `${annee}-${mois}-${jourDuMois}`;
}
/* =========================================================
   BLOC 4 - STATES PRINCIPAUX
========================================================= */
  const [planningDate, setPlanningDate] = useState(getDateJourPlanning("Lundi"));
  const [planningHeure, setPlanningHeure] = useState("10:00");
  const [planningDuree, setPlanningDuree] = useState("1h");
  const [actionsClub, setActionsClub] = useState([]);
  const [historiquesClub, setHistoriquesClub] = useState([]);
  const [toutesActions, setToutesActions] = useState([]);
  const [planning, setPlanning] = useState([]);
  const [propositionRdvInitiale, setPropositionRdvInitiale] = useState(null);
  const [contextePrime, setContextePrime] = useState(null);
  const [objectifs, setObjectifs] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
const [magasinsClub, setMagasinsClub] = useState([]);
const [magasinsClubSupprimes, setMagasinsClubSupprimes] = useState([]);
const [contactsClub, setContactsClub] = useState([]);
const [afficherToutesActionsClub, setAfficherToutesActionsClub] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginMessage, setLoginMessage] = useState("");

  const [userConnected, setUserConnected] = useState(null);
  const [screen, setScreen] = useState("dashboard");
  const [codeClientInterclubInitial, setCodeClientInterclubInitial] = useState("");
  const [clubSelected, setClubSelected] = useState(null);
  const [screenRetourClub, setScreenRetourClub] = useState("dashboard");
  const [devisInitialId, setDevisInitialId] = useState(null);
  const [devisDepuisGeneral, setDevisDepuisGeneral] = useState(false);
  const [devisInterclubInitialId, setDevisInterclubInitialId] = useState(null);
  const [contratInitialId, setContratInitialId] = useState(null);
  const [contratRenouvelerId, setContratRenouvelerId] = useState(null);
  const [boutiqueClubClubInitial, setBoutiqueClubClubInitial] = useState(null);
  const [boutiqueClubDestinationInitiale, setBoutiqueClubDestinationInitiale] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [resumeDevisDashboard, setResumeDevisDashboard] = useState(null);
  const [documentsInterclub, setDocumentsInterclub] = useState([]);
  const [erreurDocumentsInterclub, setErreurDocumentsInterclub] = useState(false);
  const [, setDerniereMajInterclubDirecte] = useState(null);

  const [sportsOptions, setSportsOptions] = useState(() => {
    try {
      const data = localStorage.getItem("sportsOptions");
      return data
        ? JSON.parse(data)
        : [
            "Football",
            "Basket",
            "Rugby",
            "Running",
            "Handball",
            "Tennis",
            "Volley",
            "Natation",
            "Cyclisme",
            "Multisport"
          ];
    } catch (e) {
      console.log("ERREUR localStorage sportsOptions :", e);
      return [
        "Football",
        "Basket",
        "Rugby",
        "Running",
        "Handball",
        "Tennis",
        "Volley",
        "Natation",
        "Cyclisme",
        "Multisport"
      ];
    }
  });

  const [historiqueTypeOptions, setHistoriqueTypeOptions] = useState(() => {
    try {
      const data = localStorage.getItem("historiqueTypeOptions");
      return data
        ? JSON.parse(data)
        : ["Concurrence", "Équipement saison", "Observation"];
    } catch (e) {
      console.log("ERREUR localStorage historiqueTypeOptions :", e);
      return ["Concurrence", "Équipement saison", "Observation"];
    }
  });

  const [nomClub, setNomClub] = useState("");
  const [villeClub, setVilleClub] = useState("");
  const [adherentsClub, setAdherentsClub] = useState("");
  const [potentielClub, setPotentielClub] = useState("");
  const [numeroCompteInterclub, setNumeroCompteInterclub] = useState("");
  const [sportClub, setSportClub] = useState("");
  const [nouveauSportClub, setNouveauSportClub] = useState("");

  const [magasinsCreation, setMagasinsCreation] = useState([
    { nom: "L’Arbresle", taux: "100" }
  ]);

  const [contactNom, setContactNom] = useState("");
  const [contactFonction, setContactFonction] = useState("");
  const [contactTelephone, setContactTelephone] = useState("");
  const [contactMail, setContactMail] = useState("");
  const [contactEditionId, setContactEditionId] = useState(null);
  const [contactEdition, setContactEdition] = useState(null);
  const [piecesClub, setPiecesClub] = useState([]);
  const aujourdHui = new Date();
  const dateDuJourInput = aujourdHui.toISOString().split("T")[0];
  const moisDuJourInput = `${aujourdHui.getFullYear()}-${String(
    aujourdHui.getMonth() + 1
  ).padStart(2, "0")}`;

  const [actionType, setActionType] = useState("Visite club");
  const [actionDate, setActionDate] = useState(dateDuJourInput);
  const [actionCommentaire, setActionCommentaire] = useState("");
  const [actionMontantTTC, setActionMontantTTC] = useState("");
  const [, setActionFiltreDocument] = useState("");
const [rechercheActionDocument, setRechercheActionDocument] = useState("");
  const [historiqueType, setHistoriqueType] = useState("Concurrence");
  const [historiqueNouveauType, setHistoriqueNouveauType] = useState("");
  const [historiqueDate, setHistoriqueDate] = useState(moisDuJourInput);
  const [historiqueCommentaire, setHistoriqueCommentaire] = useState("");

  const [planningJour, setPlanningJour] = useState("Lundi");
  const [planningType, setPlanningType] = useState("Visite club");
  const [planningCommentaire, setPlanningCommentaire] = useState("");
  const [planningFiltreCommercial, setPlanningFiltreCommercial] = useState("Tous");
const [planningFiltreSport, setPlanningFiltreSport] = useState("Tous les sports");
const [planningRechercheClub, setPlanningRechercheClub] = useState("");
const [filtrePlanningPeriode, setFiltrePlanningPeriode] = useState("Semaine en cours");
const [planningMoisFiltre, setPlanningMoisFiltre] = useState(moisDuJourInput);

  const [filtreCommercial, setFiltreCommercial] = useState(() => lireFiltreDashboard("commercial", "Moi"));
  const [filtrePeriode, setFiltrePeriode] = useState(() => lireFiltreDashboard("periode", "Toutes périodes"));
  const [exerciceSelectionne, setExerciceSelectionne] = useState("2026");
  const [dateDepuis, setDateDepuis] = useState(dateDuJourInput);
  const semaineIsoCourante = getSemaineIso(aujourdHui);
  const [anneeSelectionnee, setAnneeSelectionnee] = useState(() => lireFiltreDashboard("annee", String(aujourdHui.getFullYear())));
  const [moisSelectionne, setMoisSelectionne] = useState(() => lireFiltreDashboard("mois", String(aujourdHui.getMonth() + 1)));
  const [semaineSelectionnee, setSemaineSelectionnee] = useState(
    String(semaineIsoCourante.semaine)
  );
  const anneesDashboard = Array.from(
    { length: 8 },
    (_, index) => aujourdHui.getFullYear() - 4 + index
  );
  const moisDashboard = [
    "Janvier",
    "Février",
    "Mars",
    "Avril",
    "Mai",
    "Juin",
    "Juillet",
    "Août",
    "Septembre",
    "Octobre",
    "Novembre",
    "Décembre"
  ];
  const nombreSemainesSelectionne = getNombreSemainesIso(anneeSelectionnee);
  const lundiSemaineSelectionnee = getLundiSemaineIso(
    anneeSelectionnee,
    semaineSelectionnee
  );
  const dimancheSemaineSelectionnee = new Date(lundiSemaineSelectionnee);
  dimancheSemaineSelectionnee.setDate(lundiSemaineSelectionnee.getDate() + 6);
  const [filtreSport, setFiltreSport] = useState(() => lireFiltreDashboard("sport", "Tous les sports"));
  const [filtreFinancier, setFiltreFinancier] = useState(() => lireFiltreDashboard("financier", "Tous"));
  const [filtreBlocDashboard, setFiltreBlocDashboard] = useState("Tous");
  const [pieceNom, setPieceNom] = useState("");
  const [pieceType, setPieceType] = useState("");
  const [pieceData, setPieceData] = useState("");

  const [recherche, setRecherche] = useState(() => lireFiltreDashboard("recherche", ""));

  const [alerteDoublonClub, setAlerteDoublonClub] = useState("");
  const [alerteDoublonContact, setAlerteDoublonContact] = useState("");
  
  const [objectifCommercialSelection, setObjectifCommercialSelection] = useState("Bruno");
  const [objectifExerciceSelection, setObjectifExerciceSelection] = useState("2026");
  const [authLoading, setAuthLoading] = useState(true);
  const [modeReinitialisationMotDePasse, setModeReinitialisationMotDePasse] = useState(
    estCallbackPasswordRecovery
  );
  const [nouveauMotDePasse, setNouveauMotDePasse] = useState("");
  const [confirmationNouveauMotDePasse, setConfirmationNouveauMotDePasse] = useState("");
/* =========================================================
   BLOC 5 - USEEFFECTS DE SYNCHRO LOCALE
========================================================= */
useEffect(() => {
  setPlanningDate(getDateJourPlanning(planningJour));
}, [planningJour]);

useEffect(() => {
  localStorage.setItem("sportsOptions", JSON.stringify(sportsOptions));
}, [sportsOptions]);

useEffect(() => {
  localStorage.setItem(
    "historiqueTypeOptions",
    JSON.stringify(historiqueTypeOptions)
  );
}, [historiqueTypeOptions]);

useEffect(() => {
  memoriserFiltresDashboard({
    commercial: filtreCommercial,
    periode: filtrePeriode,
    annee: anneeSelectionnee,
    mois: moisSelectionne,
    sport: filtreSport,
    financier: filtreFinancier,
    recherche
  });
}, [filtreCommercial, filtrePeriode, anneeSelectionnee, moisSelectionne, filtreSport, filtreFinancier, recherche]);

useEffect(() => {
  if (!supabaseClientActif) return undefined;

  let actif = true;
  const callbackPasswordRecovery = estCallbackPasswordRecovery();

  const sortirDuLoading = () => {
    if (!actif) return;
    setAuthLoading(false);
  };
  const traitementsAuthDifferes = new Set();

  const appliquerSession = async (session) => {
    if (!actif) return;
if (!supabaseClientActif) return;
    try {
      if (callbackPasswordRecovery) {
        setModeReinitialisationMotDePasse(true);
        setUserConnected(null);
        setAuthLoading(false);
        return;
      }

      if (!session?.user?.id) {
        setUserConnected(null);
        sortirDuLoading();
        return;
      }

      const profil = await chargerProfilUtilisateurParId(session.user.id);

      if (!actif) return;

      if (profil) {
        setUserConnected(profil);
        // La restauration initiale part de « Moi » et sélectionne le profil.
        // Les rafraîchissements de session ne doivent pas écraser ensuite le
        // commercial choisi explicitement dans le tableau de bord.
      } else {
        setUserConnected(null);
      }
    } catch (e) {
      console.log("ERREUR APPLIQUER SESSION :", e);
      if (!actif) return;
      setUserConnected(null);
    } finally {
      sortirDuLoading();
    }
  };

  const timerSecurite = setTimeout(() => {
    console.log("SECURITE AUTH LOADING : arrêt du chargement forcé");
    sortirDuLoading();
  }, 1500);

  const restaurerSession = async () => {
    try {
      const {
        data: { session }
      } = await supabaseClientActif.auth.getSession();

      await appliquerSession(session);
    } catch (e) {
      console.log("ERREUR RESTAURATION SESSION :", e);
      if (!actif) return;
      setUserConnected(null);
      sortirDuLoading();
    }
  };

  if (callbackPasswordRecovery) {
    setModeReinitialisationMotDePasse(true);
    setUserConnected(null);
    setAuthLoading(false);
  } else {
    restaurerSession();
  }


const {
  data: { subscription }
} = supabaseClientActif.auth.onAuthStateChange((event, session) => {

  if (event === "PASSWORD_RECOVERY" || callbackPasswordRecovery) {
    setModeReinitialisationMotDePasse(true);
    setUserConnected(null);
    setAuthLoading(false);
    return;
  }

  // La session initiale est déjà restaurée par restaurerSession().
  if (event === "INITIAL_SESSION") return;

  const traitement = planifierApresVerrouAuth(async () => {
    traitementsAuthDifferes.delete(traitement);
    if (!actif) return;
    await appliquerSession(session);
  });
  traitementsAuthDifferes.add(traitement);
});


  return () => {
    actif = false;
    clearTimeout(timerSecurite);
    traitementsAuthDifferes.forEach(clearTimeout);
    traitementsAuthDifferes.clear();
    subscription.unsubscribe();
  };
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [supabaseClientActif]);

useEffect(() => {
  if (!supabaseClientActif || !userConnected?.id) return undefined;
  let controleActif = true;
  let deconnexionEnCours = false;
  let controleEnCours = false;
  let controleTemporise = null;

  const planifierControle = () => {
    clearTimeout(controleTemporise);
    if (!controleActif || deconnexionEnCours || (typeof document !== "undefined" && document.hidden)) return;
    controleTemporise = setTimeout(verifierCompteActif, DELAI_CONTROLE_COMPTE_ACTIF_MS);
  };

  const verifierCompteActif = async () => {
    if (controleEnCours || deconnexionEnCours) return;
    controleEnCours = true;
    try {
      const { data, error } = await supabaseClientActif
        .from("utilisateurs_profils")
        .select("actif,renouvellement_mdp_requis")
        .eq("id", userConnected.id)
        .maybeSingle();
      if (!controleActif || error || (data?.actif === true && data?.renouvellement_mdp_requis !== true) || deconnexionEnCours) return;
      deconnexionEnCours = true;
      await supabaseClientActif.auth.signOut({ scope: "local" });
      if (!controleActif) return;
      setUserConnected(null);
      setScreen("dashboard");
      alert(data?.renouvellement_mdp_requis
        ? "Le renouvellement de votre mot de passe est obligatoire. Utilisez le lien reçu par email."
        : "Votre compte a été désactivé. Vous avez été déconnecté.");
    } finally {
      controleEnCours = false;
      planifierControle();
    }
  };

  const gererVisibiliteCompte = () => {
    clearTimeout(controleTemporise);
    if (!document.hidden) verifierCompteActif();
  };

  verifierCompteActif();
  if (typeof document !== "undefined") document.addEventListener("visibilitychange", gererVisibiliteCompte);
  return () => {
    controleActif = false;
    clearTimeout(controleTemporise);
    if (typeof document !== "undefined") document.removeEventListener("visibilitychange", gererVisibiliteCompte);
  };
}, [supabaseClientActif, userConnected?.id]);
/* =========================================================
   BLOC 6 - CHARGEMENT DES DONNÉES SUPABASE
========================================================= */
const chargerPiecesClub = async (clubId) => {
  if (!clubId) {
    setPiecesClub([]);
    return;
  }

  if (!supabaseClientActif) return;

  const { data, error } = await supabaseClientActif
    .from("pieces_jointes")
    .select("*")
    .eq("club_id", clubId)
    .order("created_at", { ascending: false });

  if (error) {
    console.log("ERREUR FETCH PIECES :", error);
    setPiecesClub([]);
    return;
  }

  const piecesAvecAcces = await Promise.all((data || []).map(async (piece) => {
    if (!piece.chemin) return piece;
    const { data: acces, error: accesError } = await supabaseClientActif.storage
      .from("pieces-clubs")
      .createSignedUrl(piece.chemin, 3600);
    if (accesError) {
      console.log("ERREUR URL SIGNEE PIECE :", accesError);
      return piece;
    }
    return { ...piece, url: acces?.signedUrl || "" };
  }));

  setPiecesClub(piecesAvecAcces);
};

const chargerClubs = async () => {
 if (!supabaseClientActif) return;

const { data, error } = await supabaseClientActif
.from("clubs")
.select("*")
.eq("groupe", groupeSelectionne)

if (error) {
  console.log("ERREUR clubs :", error);
  return;
}

  if (error) {
    console.log("ERROR FETCH CLUBS :", error);
    setClubs([]);
    return;
  }

  setClubs(appliquerAttributionsCommercialesAuditees(data || []));
};

const chargerTousContacts = async () => {
if (!supabaseClientActif) return;

const { data, error } = await supabaseClientActif
  .from("contacts")
  .select("*")
  .eq("groupe", groupeSelectionne)

if (error) {
  console.log("ERREUR contacts :", error);
  setAllContacts([]);
  return;
}

setAllContacts(data || []);
};

const chargerToutesActions = async () => {
  if (!supabaseClientActif) return;

  const { data, error } = await supabaseClientActif
    .from("actions")
    .select("*")
    .eq("groupe", groupeSelectionne);

  if (error) {
    console.log("ERREUR FETCH TOUTES ACTIONS :", error);
    setToutesActions([]);
    return;
  }

  setToutesActions(data || []);
};

const chargerDocumentsInterclub = async () => {
  if (!supabaseClientActif || !userConnected?.id) return;
  const taillePage = 1000;
  let debut = 0;
  const documents = [];

  while (true) {
    const { data, error } = await supabaseClientActif
      .from("interclub_sync_documents")
      .select("id,type_document,identifiant_source,donnees,derniere_observation,updated_at")
      .eq("groupe", groupeSelectionne)
      .order("id", { ascending: true })
      .range(debut, debut + taillePage - 1);

    if (error) {
      console.log("ERREUR FETCH DOCUMENTS INTERCLUB :", error.code || error.message);
      setErreurDocumentsInterclub(true);
      return;
    }

    documents.push(...(data || []));
    if (!data || data.length < taillePage) break;
    debut += taillePage;
  }

  // La structure marge V2 est additive. Tant que sa migration locale n'est pas
  // appliquée à un environnement, l'absence de table laisse simplement les
  // marges au statut « non collectée » sans perturber le moteur CA existant.
  const mouvementsMarge = [];
  for (let margeDebut = 0; ; margeDebut += taillePage) {
    const lecture = await supabaseClientActif
      .from("interclub_marge_mouvements")
      .select("id,groupe,magasin_source,commande_numero,commercial_commande,type_mouvement,document_numero,date_mouvement,ca_ht_mouvement,ca_ttc_mouvement,marge_euros,sens,source_marge,certification,lignes_marge,numero_facture_origine,code_client")
      .eq("groupe", groupeSelectionne)
      .order("id", { ascending: true })
      .range(margeDebut, margeDebut + taillePage - 1);
    if (lecture.error) {
      if (!['42P01', 'PGRST205'].includes(lecture.error.code)) console.log("MARGE V2 INDISPONIBLE :", lecture.error.code || lecture.error.message);
      break;
    }
    const lignes = lecture.data || [];
    mouvementsMarge.push(...lignes.map((ligne) => ({
      id: ligne.id,
      type_document: 'MARGE_FACTUREE',
      identifiant_source: ligne.document_numero,
      donnees: {
        type_mouvement: ligne.type_mouvement,
        numero_document: ligne.document_numero,
        numero_commande: ligne.commande_numero,
        commercial_commande: ligne.commercial_commande,
        date_mouvement: ligne.date_mouvement,
        ca_ht_mouvement: ligne.ca_ht_mouvement,
        ca_ttc_mouvement: ligne.ca_ttc_mouvement,
        marge_euros: ligne.marge_euros,
        sens: ligne.sens,
        source_marge: ligne.source_marge,
        certification: ligne.certification,
        lignes_marge: ligne.lignes_marge,
        numero_facture_origine: ligne.numero_facture_origine,
        code_client: ligne.code_client,
      }
    })));
    if (lignes.length < taillePage) break;
  }

  setDocumentsInterclub([...documents, ...mouvementsMarge]);
  setErreurDocumentsInterclub(false);
  const { data: parametres } = await supabaseClientActif
    .from("interclub_sync_parametres")
    .select("derniere_directe_reussie")
    .eq("groupe", groupeSelectionne)
    .maybeSingle();
  setDerniereMajInterclubDirecte(parametres?.derniere_directe_reussie || null);
};

const chargerPlanning = async () => {
  if (!supabaseClientActif) {
    setPlanning([]);
    return;
  }
  const { data, error } = await supabaseClientActif
    .from("planning")
    .select("*")
    .eq("groupe", groupeSelectionne)
    .order("id", { ascending: false });

  if (error) {
    console.log("ERREUR FETCH PLANNING :", error);
    setPlanning([]);
    return;
  }

  setPlanning(data || []);
};

const chargerObjectifs = async () => {
  const { data, error } = await supabase
    .from("objectifs")
    .select("*")
    .order("annee_reference", { ascending: false })
    .order("mois", { ascending: true });

  if (error) {
    console.log("ERREUR FETCH OBJECTIFS :", error);
    setObjectifs([]);
    return;
  }

  setObjectifs(data || []);
};

const chargerMagasinsClub = async (clubId) => {
  if (!clubId) {
    setMagasinsClub([]);
    return;
  }

  const { data, error } = await supabase
    .from("magasins_club")
    .select("*")
    .eq("club_id", clubId);

  if (error) {
    console.log("ERREUR FETCH MAGASINS :", error);
    setMagasinsClub([]);
    return;
  }

  setMagasinsClub(data || []);
  setMagasinsClubSupprimes([]);
};

const chargerContactsClub = async (clubId) => {
  if (!clubId) {
    setContactsClub([]);
    return;
  }

  const { data, error } = await supabase
    .from("contacts")
    .select("*")
    .eq("club_id", clubId);

  if (error) {
    console.log("ERREUR FETCH CONTACTS :", error);
    setContactsClub([]);
    return;
  }

  setContactsClub(data || []);
};

const chargerActionsClub = async (clubId) => {
  if (!clubId) {
    setActionsClub([]);
    return;
  }

  const { data, error } = await supabase
    .from("actions")
    .select("*")
    .eq("club_id", clubId)
    .order("date", { ascending: false });

  if (error) {
    console.log("ERREUR FETCH ACTIONS :", error);
    setActionsClub([]);
    return;
  }

  setActionsClub(data || []);
};

const chargerHistoriquesClub = async (clubId) => {
  if (!clubId) {
    setHistoriquesClub([]);
    return;
  }

  const { data, error } = await supabase
    .from("historiques")
    .select("*")
    .eq("club_id", clubId)
    .order("date", { ascending: false });

  if (error) {
    console.log("ERREUR FETCH HISTORIQUES :", error);
    setHistoriquesClub([]);
    return;
  }

  setHistoriquesClub(data || []);
};

const chargerDetailClub = async (club) => {
  setClubSelected(club);
setActionFiltreDocument("");
  const clubId = club?.id || club?.identifiant;

  await Promise.all([
    chargerMagasinsClub(clubId),
    chargerContactsClub(clubId),
    chargerActionsClub(clubId),
    chargerHistoriquesClub(clubId),
    chargerPiecesClub(clubId)
  ]);
};
/* =========================================================
   BLOC 7 - CHARGEMENT INITIAL APPLICATION
========================================================= */
// eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => {
  if (!supabaseClientActif || authLoading || !userConnected?.id) return;

  chargerClubs();
  chargerTousContacts();
  chargerToutesActions();
  chargerPlanning();
  chargerObjectifs();

// eslint-disable-next-line react-hooks/exhaustive-deps
}, [supabaseClientActif, authLoading, userConnected?.id, groupeSelectionne]);

useEffect(() => {
  if (!supabaseClientActif || !userConnected?.id) {
    setDocumentsInterclub([]);
    return;
  }
  chargerDocumentsInterclub();
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [supabaseClientActif, userConnected?.id, groupeSelectionne]);
/* =========================================================
   BLOC 8 - CONNEXION ET ACTIONS GLOBALES
========================================================= */

const supprimerContact = async (idContact) => {
  const confirmation = window.confirm(
    "Voulez-vous vraiment supprimer ce contact ?"
  );
  if (!confirmation) return;

  const { error } = await supabase
    .from("contacts")
    .delete()
    .eq("id", idContact);

  if (error) {
    console.log("ERREUR DELETE CONTACT :", error);
    alert("Erreur lors de la suppression du contact");
    return;
  }

  setContactsClub((prev) => prev.filter((c) => c.id !== idContact));
  setAllContacts((prev) => prev.filter((c) => c.id !== idContact));
};

const enregistrerModificationContact = async () => {
  try {
    const clubId=clubSelected?.id||clubSelected?.identifiant;
    const contact=await modifierContactCRM(supabaseClientActif,clubId,contactEdition);
    setContactsClub((liste)=>liste.map((item)=>item.id===contact.id?contact:item));
    setAllContacts((liste)=>liste.map((item)=>item.id===contact.id?contact:item));
    setContactEditionId(null); setContactEdition(null);
  } catch (error) {
    console.log('ERREUR UPDATE CONTACT :',error);
    alert(error?.message||'Erreur lors de la modification du contact');
  }
};

/* =========================
   LOGIN CORRIGÉ + DEBUG
========================= */
const handleLogin = async () => {
  const usernameNettoye = String(username || "").trim().toLowerCase();
  const passwordNettoye = String(password || "");
  if (!supabaseClientActif) {
  setLoginMessage("Connexion en cours d'initialisation. Réessayez dans une seconde.");
  alert("Connexion en cours d'initialisation. Merci de réessayer dans une seconde.");
  return;
}

  if (!usernameNettoye || !passwordNettoye) {
    setLoginMessage("Renseignez l'utilisateur et le mot de passe.");
    alert("Merci de renseigner l'utilisateur et le mot de passe");
    return;
  }

  try {
    setLoginMessage("Connexion en cours…");
    let userId = null;
    let profilFallback = null;

    if (AUTH_USERNAME_EDGE_ACTIVE) {
      const { data: edgeData, error: edgeError } =
        await supabaseBootstrap.functions.invoke("auth-username", {
          body: {
            action: "login",
            groupe: groupeSelectionne,
            username: usernameNettoye,
            password: passwordNettoye
          }
        });

      if (
        edgeError ||
        !edgeData?.access_token ||
        !edgeData?.refresh_token
      ) {
        console.log("ERREUR LOGIN AUTH-USERNAME :", edgeError);
        alert("Mot de passe incorrect ou compte non configuré dans Supabase Auth");
        return;
      }

      const { data: sessionData, error: sessionError } =
        await supabaseClientActif.auth.setSession({
          access_token: edgeData.access_token,
          refresh_token: edgeData.refresh_token
        });

      userId = sessionData?.user?.id || sessionData?.session?.user?.id || null;
      if (sessionError || !userId) {
        console.log("ERREUR INSTALLATION SESSION :", sessionError);
        alert("Erreur de connexion");
        return;
      }
    } else {
      // FALLBACK TEMPORAIRE ÉTAPE 35 : ancien parcours pré-authentification.
      const profil = await chargerProfilUtilisateurParUsername(usernameNettoye);
      profilFallback = profil;

      if (!profil || !profil.email) {
        setLoginMessage("Utilisateur introuvable dans la recette ou non visible avec les droits courants.");
        alert(
          "Connexion impossible : utilisateur introuvable (table utilisateurs_profils ou RLS)"
        );
        return;
      }

      const { data, error } = await supabaseClientActif.auth.signInWithPassword({
        email: profil.email,
        password: passwordNettoye
      });

      if (error || !data?.user) {
        console.log("ERREUR LOGIN AUTH :", error);
        setLoginMessage(`Connexion Auth refusée : ${error?.message || "compte non configuré"}`);
        alert("Mot de passe incorrect ou compte non configuré dans Supabase Auth");
        return;
      }

      userId = data.user.id;
    }

    const profilRecharge = await chargerProfilUtilisateurParId(userId);
    console.log("LOGIN - profil rechargé :", profilRecharge);

    if (profilRecharge) {
      setUserConnected(profilRecharge);
    } else if (profilFallback) {
      setUserConnected(profilFallback);
    } else {
      setLoginMessage("Connexion réussie, mais le profil actif est introuvable.");
      alert("Connexion réussie, mais profil utilisateur introuvable.");
      return;
    }

    setPassword("");
    setLoginMessage("");

  } catch (e) {
    console.log("ERREUR HANDLE LOGIN :", e);
    setLoginMessage(`Erreur de connexion : ${e?.message || "erreur inconnue"}`);
    alert("Erreur de connexion");
  }
};

const handleMotDePasseOublie = async () => {
  if (!supabaseClientActif) {
    alert("Connexion en cours d'initialisation. Merci de réessayer dans une seconde.");
    return;
  }

  const identifiantNettoye = String(username || "").trim().toLowerCase();
  if (!identifiantNettoye) {
    alert("Saisissez d'abord votre nom d'utilisateur.");
    return;
  }

  try {
    if (AUTH_USERNAME_EDGE_ACTIVE) {
      const { error } = await supabaseBootstrap.functions.invoke(
        "auth-username",
        {
          body: {
            action: "reset-password",
            groupe: groupeSelectionne,
            username: identifiantNettoye
          }
        }
      );

      if (error) {
        console.log("ERREUR RESET AUTH-USERNAME :", error);
        alert("Erreur technique lors de l'envoi de la demande.");
        return;
      }

      alert(
        "Si ce compte existe, un email a été envoyé. Attendez au moins 60 secondes avant de renouveler la demande."
      );
      return;
    }

    // FALLBACK TEMPORAIRE ÉTAPE 35 : ancien parcours pré-authentification.
    const profil = await chargerProfilUtilisateurParUsername(identifiantNettoye);
    if (!profil?.email) {
      alert("Utilisateur ou email introuvable.");
      return;
    }

    const redirectTo = `${window.location.origin}${window.location.pathname}?groupe=${encodeURIComponent(groupeSelectionne)}`;
    const { error } = await supabaseClientActif.auth.resetPasswordForEmail(
      profil.email,
      { redirectTo }
    );

    if (error) {
      console.log("ERREUR RESET PASSWORD :", error);
      alert("Erreur technique lors de l'envoi de la demande.");
      return;
    }

    alert("Demande envoyée. Consultez votre messagerie.");
  } catch (e) {
    console.log("ERREUR MOT DE PASSE OUBLIE :", e);
    alert("Erreur technique lors de l'envoi de la demande.");
  }
};

const handleModifierMotDePasse = async () => {
  if (nouveauMotDePasse !== confirmationNouveauMotDePasse) {
    alert("Les mots de passe sont différents.");
    return;
  }

  if (!nouveauMotDePasse) {
    alert("Merci de saisir un nouveau mot de passe.");
    return;
  }

  try {
    const { data: { session } } = await supabaseClientActif.auth.getSession();
    if (!session?.user) {
      alert("Erreur technique : lien expiré ou invalide.");
      return;
    }

    const { error } = await supabaseClientActif.auth.updateUser({
      password: nouveauMotDePasse
    });

    if (error) {
      console.log("ERREUR UPDATE PASSWORD :", error);
      alert("Erreur technique lors de la modification du mot de passe.");
      return;
    }

    await supabaseClientActif.auth.signOut({ scope: "local" });
    setNouveauMotDePasse("");
    setConfirmationNouveauMotDePasse("");
    setModeReinitialisationMotDePasse(false);
    setUserConnected(null);
    window.history.replaceState(
      {},
      document.title,
      `${window.location.pathname}?groupe=${encodeURIComponent(groupeSelectionne)}`
    );
    alert("Votre mot de passe a été modifié.");
  } catch (e) {
    console.log("ERREUR MODIFICATION MOT DE PASSE :", e);
    alert("Erreur technique lors de la modification du mot de passe.");
  }
};

/* =========================
   LOGOUT
========================= */
const handleLogout = async () => {
  const { error } = await supabaseClientActif.auth.signOut();

  if (error) {
    console.log("ERREUR LOGOUT :", error);
    alert("Erreur lors de la déconnexion");
    return;
  }

  setUserConnected(null);
  setUsername("");
  setPassword("");
  oublierFiltresDashboard();
  setFiltreCommercial("Moi");
  setScreen("dashboard");
  setClubSelected(null);
};
/* =========================================================
   BLOC 9 - VARIABLES DÉRIVÉES ET STYLE
========================================================= */
  // Le code est attribue sous verrou par la base, jamais depuis la liste React.

const couleurCommercial = userConnected?.couleur || "#f2f2f2"; 

const stylePage = {
  minHeight: "100vh",
  background: couleurCommercial,
  padding: 12,
  fontFamily: "Arial"
};

const dashboardFilterContext = creerDashboardFilterContext({
  groupe: groupeSelectionne,
  utilisateur: userConnected,
  commercial: filtreCommercial,
  modePeriode: filtrePeriode,
  annee: anneeSelectionnee,
  mois: moisSelectionne,
  exercice: exerciceSelectionne,
  semaine: semaineSelectionnee,
  dateDepuis,
  sport: filtreSport,
  statutFinancier: filtreFinancier,
  recherche
});
const commercialActif = dashboardFilterContext.commercialAnalyse;
const normaliserCommercial = (valeur) =>
  String(valeur || "").trim().toLocaleLowerCase("fr-FR");
const commercialActifNormalise = normaliserCommercial(commercialActif);
const touteEquipeSelectionnee =
  commercialActifNormalise === normaliserCommercial("Toute l'équipe") ||
  commercialActifNormalise === normaliserCommercial("Tous");
const rechercheMin = recherche.trim().toLowerCase();

function clubCorrespondAuxFiltresGeneraux(club, { ignorerCommercial = false } = {}) {
  const clubCommercial = normaliserCommercial(club.commercial) || 'non attribué';
  const commercialOk =
    ignorerCommercial ||
    touteEquipeSelectionnee ||
    clubCommercial === commercialActifNormalise;
  if (!commercialOk) return false;

  const sportOk =
    filtreSport === "Tous les sports" || (club.sport || "") === filtreSport;
  if (!sportOk) return false;
  if (rechercheMin === "") return true;

  const clubId = club.id || club.identifiant;
  const contactsDuClub = allContacts.filter(
    (contact) => String(contact.club_id) === String(clubId)
  );
  const clubMatch = [club.code, club.nom, club.ville, club.sport].some((valeur) =>
    String(valeur || "").toLowerCase().includes(rechercheMin)
  );
  const contactMatch = contactsDuClub.some((contact) =>
    [contact.nom, contact.telephone, contact.mail].some((valeur) =>
      String(valeur || "").toLowerCase().includes(rechercheMin)
    )
  );
  return clubMatch || contactMatch;
}

const clubsFiltresGeneraux = clubs.filter(clubCorrespondAuxFiltresGeneraux);
// Portefeuille affichable : Sport/Recherche s'appliquent à la liste, tandis
// que l'attribution financière reste celle de la commande.
const clubsFiltresHorsCommercial = clubs.filter((club) =>
  clubCorrespondAuxFiltresGeneraux(club, { ignorerCommercial: true })
);
const idsClubsFiltresGeneraux = new Set(
  clubsFiltresGeneraux.map((club) => String(club.id || club.identifiant))
);
/* =========================================================
   BLOC 10 - FILTRES TEMPORELS ET ACTIONS FILTRÉES
========================================================= */
function getDatePremierJourMois() {
  return new Date(Number(anneeSelectionnee), Number(moisSelectionne) - 1, 1);
}
function getDateFinFiltreDashboard() {
  if (filtrePeriode === "Toutes périodes") {
    return null;
  }

  if (filtrePeriode === "Semaine") {
    const lundi = getLundiSemaineIso(anneeSelectionnee, semaineSelectionnee);
    return getDimancheSemaine(lundi);
  }

  if (filtrePeriode === "Mois") {
    const debutMoisSuivant = getDatePremierJourMoisSuivant();
    const fin = new Date(debutMoisSuivant);
    fin.setMilliseconds(fin.getMilliseconds() - 1);
    return fin;
  }

  if (filtrePeriode === "Cumul analysé") {
    const debutMoisSuivant = getDatePremierJourMoisSuivant();
    const fin = new Date(debutMoisSuivant);
    fin.setMilliseconds(fin.getMilliseconds() - 1);
    return fin;
  }

  if (filtrePeriode === "Exercice fiscal") {
    const exerciceDebut = Number(exerciceSelectionne);
    return new Date(exerciceDebut + 1, 2, 31, 23, 59, 59, 999);
  }

  if (filtrePeriode === "Année") {
    return new Date(Number(anneeSelectionnee), 11, 31, 23, 59, 59, 999);
  }

  if (filtrePeriode === "Depuis le") {
    return new Date();
  }

  return null;
}

function getDateDebutFiltreDashboard() {
  if (filtrePeriode === "Toutes périodes") return null;
  if (filtrePeriode === "Semaine") {
    return getLundiSemaineIso(anneeSelectionnee, semaineSelectionnee);
  }
  if (filtrePeriode === "Mois") return getDatePremierJourMois();
  if (filtrePeriode === "Cumul analysé") {
    const annee = Number(anneeSelectionnee);
    const mois = Number(moisSelectionne);
    return new Date(mois >= 4 ? annee : annee - 1, 3, 1, 0, 0, 0, 0);
  }
  if (filtrePeriode === "Exercice fiscal") {
    return new Date(Number(exerciceSelectionne), 3, 1, 0, 0, 0, 0);
  }
  if (filtrePeriode === "Année") {
    return new Date(Number(anneeSelectionnee), 0, 1, 0, 0, 0, 0);
  }
  if (filtrePeriode === "Depuis le") return parseInputDate(dateDepuis);
  return null;
}

function formatDateFiltreDashboard(date) {
  if (!date) return "";
  const annee = date.getFullYear();
  const mois = String(date.getMonth() + 1).padStart(2, "0");
  const jour = String(date.getDate()).padStart(2, "0");
  return `${annee}-${mois}-${jour}`;
}

function getDatePremierJourMoisSuivant() {
  return new Date(Number(anneeSelectionnee), Number(moisSelectionne), 1);
}

function parseInputDate(dateString) {
  if (!dateString) return null;
  const d = new Date(dateString + "T00:00:00");
  return isNaN(d.getTime()) ? null : d;
}

function getExerciceFiscal(date) {
  const mois = date.getMonth() + 1;
  const annee = date.getFullYear();
  return mois >= 4 ? annee : annee - 1;
}

function isActionDansPeriode(action) {
  if (filtrePeriode === "Toutes périodes") return true;
  if (!action.date) return false;

  const actionDateObj = parseInputDate(action.date);
  if (!actionDateObj) return false;

  if (filtrePeriode === "Semaine") {
    const lundi = getLundiSemaineIso(anneeSelectionnee, semaineSelectionnee);
    const dimanche = getDimancheSemaine(lundi);
    return actionDateObj >= lundi && actionDateObj <= dimanche;
  }

if (filtrePeriode === "Mois") {
  return (
    actionDateObj >= getDatePremierJourMois() &&
    actionDateObj < getDatePremierJourMoisSuivant()
  );
}

if (filtrePeriode === "Cumul analysé") {
  return (
    actionDateObj >= getDateDebutFiltreDashboard() &&
    actionDateObj <= getDateFinFiltreDashboard()
  );
}

if (filtrePeriode === "Exercice fiscal") {
  return getExerciceFiscal(actionDateObj) === Number(exerciceSelectionne);
}

  if (filtrePeriode === "Année") {
    return actionDateObj.getFullYear() === Number(anneeSelectionnee);
  }

  if (filtrePeriode === "Depuis le") {
    const dateRef = parseInputDate(dateDepuis);
    if (!dateRef) return true;
    const finAujourdHui = new Date();
    finAujourdHui.setHours(23, 59, 59, 999);
    return actionDateObj >= dateRef && actionDateObj <= finAujourdHui;
  }

  return true;
}
function getLundiSemaine(dateBase = new Date()) {
  const d = new Date(dateBase);
  const jour = d.getDay();
  const diff = jour === 0 ? -6 : 1 - jour;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getDimancheSemaine(dateBase = new Date()) {
  const lundi = getLundiSemaine(dateBase);
  const dimanche = new Date(lundi);
  dimanche.setDate(lundi.getDate() + 6);
  dimanche.setHours(23, 59, 59, 999);
  return dimanche;
}

function addDays(dateBase, nbJours) {
  const d = new Date(dateBase);
  d.setDate(d.getDate() + nbJours);
  return d;
}

function getPremierJourMoisDepuisDate(dateBase = new Date()) {
  return new Date(dateBase.getFullYear(), dateBase.getMonth(), 1, 0, 0, 0, 0);
}

function getDernierJourMoisDepuisDate(dateBase = new Date()) {
  return new Date(dateBase.getFullYear(), dateBase.getMonth() + 1, 0, 23, 59, 59, 999);
}

function isPlanningDansPeriode(ligne) {
  if (!ligne?.date_rdv) return false;

  const dateLigne = new Date(`${ligne.date_rdv}T00:00:00`);
  if (isNaN(dateLigne.getTime())) return false;

  const aujourdHui = new Date();

  if (filtrePlanningPeriode === "Semaine en cours") {
    const debut = getLundiSemaine(aujourdHui);
    const fin = getDimancheSemaine(aujourdHui);
    return dateLigne >= debut && dateLigne <= fin;
  }

  if (filtrePlanningPeriode === "Semaine précédente") {
    const base = addDays(aujourdHui, -7);
    const debut = getLundiSemaine(base);
    const fin = getDimancheSemaine(base);
    return dateLigne >= debut && dateLigne <= fin;
  }

  if (filtrePlanningPeriode === "Semaine suivante") {
    const base = addDays(aujourdHui, 7);
    const debut = getLundiSemaine(base);
    const fin = getDimancheSemaine(base);
    return dateLigne >= debut && dateLigne <= fin;
  }

  if (filtrePlanningPeriode === "Mois en cours") {
    const debut = getPremierJourMoisDepuisDate(aujourdHui);
    const fin = getDernierJourMoisDepuisDate(aujourdHui);
    return dateLigne >= debut && dateLigne <= fin;
  }

  if (filtrePlanningPeriode === "Mois précédent") {
    const base = new Date(aujourdHui.getFullYear(), aujourdHui.getMonth() - 1, 1);
    const debut = getPremierJourMoisDepuisDate(base);
    const fin = getDernierJourMoisDepuisDate(base);
    return dateLigne >= debut && dateLigne <= fin;
  }

  if (filtrePlanningPeriode === "Mois suivant") {
    const base = new Date(aujourdHui.getFullYear(), aujourdHui.getMonth() + 1, 1);
    const debut = getPremierJourMoisDepuisDate(base);
    const fin = getDernierJourMoisDepuisDate(base);
    return dateLigne >= debut && dateLigne <= fin;
  }

  if (filtrePlanningPeriode === "Date du mois") {
    if (!planningMoisFiltre) return true;

    const [annee, mois] = planningMoisFiltre.split("-").map(Number);
    const debut = new Date(annee, mois - 1, 1, 0, 0, 0, 0);
    const fin = new Date(annee, mois, 0, 23, 59, 59, 999);
    return dateLigne >= debut && dateLigne <= fin;
  }

  return true;
}
function getTotauxClub(club) {
  const actionsDuClub = getActionsDuClub(club, actionsFiltrees);
  return calculerTotauxActions(actionsDuClub);
}

const toutesLesActions = toutesActions.filter((action) =>
  idsClubsFiltresGeneraux.has(String(action.club_id))
);

const actionsFiltrees = toutesLesActions.filter(isActionDansPeriode);

const contexteFinancier = creerContexteFinancier(dashboardFilterContext);
const periodesCaMetier = contexteFinancier.periodes;


const objectifsFiltresCommercial =
  touteEquipeSelectionnee
    ? objectifs
    : objectifs.filter(
        (obj) =>
          normaliserCommercial(obj.commercial) === commercialActifNormalise
      );

/* =========================
   OBJECTIF MOIS CA
========================= */
const objectifsMois = objectifsFiltresCommercial.filter((obj) => {
  if (String(obj.indicateur || "").toLowerCase() !== "ca_encaisse_ttc") {
    return false;
  }

  const cleMois = `${Number(obj.annee_reference)}-${String(Number(obj.mois)).padStart(2, "0")}`;
  return cleMois === periodesCaMetier.mois.debut.slice(0, 7);
});

const objectifMoisCA = objectifsMois.reduce(
  (sum, obj) => sum + (Number(obj.valeur_objectif) || 0),
  0
);

// Les filtres Sport / Recherche / Statut ne réduisent jamais les KPI globaux.
// Le commercial métier reste porté par la commande dans le moteur financier.
const codesClientsInterclubFiltres = new Set(clubs.map((club) => String(club.numero_compte_interclub || '').trim()).filter(Boolean));
const codesClientsInterclubConnus = new Set(clubs.map((club) => String(club.numero_compte_interclub || '').trim()).filter(Boolean));
const filtreCommercialCommande = touteEquipeSelectionnee ? '' : commercialActif;
const donneesInterclubV2Disponibles = documentsInterclub.some((document) => document.type_document === 'COMMANDE')
  && documentsInterclub.some((document) => document.type_document === 'FACTURE');
const optionsInterclubV2 = {
  commercialCommande: filtreCommercialCommande,
  codesClientsConnus: codesClientsInterclubConnus,
  inclureNonRapproches: true,
};
const encaissementsInterclubDisponibles = donneesInterclubV2Disponibles
  && documentsInterclub.some((document) => document.type_document === 'PAIEMENT');
const financeCaCanonique = donneesInterclubV2Disponibles ? syntheseFinancePointCommercial(
  documentsInterclub,
  codesClientsInterclubFiltres,
  { ...optionsInterclubV2, periodes: periodesCaMetier }
) : null;
const factureMoisCA = financeCaCanonique?.mois.caTTC || 0;
const realiseMoisCA = encaissementsInterclubDisponibles ? financeCaCanonique?.mois.encaisse || 0 : 0;
const margeFactureeMois = financeCaCanonique?.mois.margeFacturee || null;
const margeEncaisseeMois = financeCaCanonique?.mois.margeEncaissee || null;

/* =========================
   OBJECTIF CUMUL CA
========================= */
const objectifsCumul = objectifsFiltresCommercial.filter((obj) => {
  if (String(obj.indicateur || "").toLowerCase() !== "ca_encaisse_ttc") {
    return false;
  }

  const cleMois = `${Number(obj.annee_reference)}-${String(Number(obj.mois)).padStart(2, "0")}`;
  return cleMois >= periodesCaMetier.cumul.debut.slice(0, 7)
    && cleMois <= periodesCaMetier.cumul.fin.slice(0, 7);
});

const objectifCumulCA = objectifsCumul.reduce(
  (sum, obj) => sum + (Number(obj.valeur_objectif) || 0),
  0
);

const objectifsCaMensuels = Array.from(objectifsCumul.reduce((parMois, obj) => {
  const cle = `${Number(obj.annee_reference)}-${String(Number(obj.mois)).padStart(2, "0")}`;
  parMois.set(cle, (parMois.get(cle) || 0) + (Number(obj.valeur_objectif) || 0));
  return parMois;
}, new Map()), ([mois, valeur]) => ({ mois, valeur }));

const factureCumulCA = financeCaCanonique?.cumul.caTTC || 0;
const realiseCumulCA = encaissementsInterclubDisponibles ? financeCaCanonique?.cumul.encaisse || 0 : 0;
const margeFactureeCumul = financeCaCanonique?.cumul.margeFacturee || null;
const margeEncaisseeCumul = financeCaCanonique?.cumul.margeEncaissee || null;

/* =========================
   OBJECTIF MOIS VISITES
========================= */
const objectifsMoisVisites = objectifsFiltresCommercial.filter((obj) => {
  if (String(obj.indicateur || "").toLowerCase() !== "visites") return false;
  const cleMois = `${Number(obj.annee_reference)}-${String(Number(obj.mois)).padStart(2, "0")}`;
  return cleMois === contexteFinancier.fluxDebut.slice(0, 7);
});

const objectifMoisVisites = objectifsMoisVisites.reduce(
  (sum, obj) => sum + (Number(obj.valeur_objectif) || 0),
  0
);

const actionsVisitesMois = planning.filter((a) => {
  if (!estVisiteRealisee(a)) return false;
  if (!idsClubsFiltresGeneraux.has(String(a.club_id))) return false;
  const date = String(a.date_rdv || '').slice(0, 10);
  return date >= contexteFinancier.fluxDebut && date <= contexteFinancier.fluxFin;
});

const realiseMoisVisites = actionsVisitesMois.length;

/* =========================
   OBJECTIF CUMUL VISITES
========================= */
const objectifsCumulVisites = objectifsFiltresCommercial.filter((obj) => {
  if (String(obj.indicateur || "").toLowerCase() !== "visites") return false;
  const cleMois = `${Number(obj.annee_reference)}-${String(Number(obj.mois)).padStart(2, "0")}`;
  return cleMois >= contexteFinancier.exerciceDebut.slice(0, 7)
    && cleMois <= contexteFinancier.cumulFin.slice(0, 7);
});

const objectifCumulVisites = objectifsCumulVisites.reduce(
  (sum, obj) => sum + (Number(obj.valeur_objectif) || 0),
  0
);

const actionsVisitesCumul = planning.filter((a) => {
  if (!estVisiteRealisee(a)) return false;
  if (!idsClubsFiltresGeneraux.has(String(a.club_id))) return false;
  const date = String(a.date_rdv || '').slice(0, 10);
  return date >= contexteFinancier.exerciceDebut && date <= contexteFinancier.cumulFin;
});

const realiseCumulVisites = actionsVisitesCumul.length;

/* =========================
   TAUX / ECARTS / STATUTS CA
========================= */
const tauxMoisCA =
  objectifMoisCA > 0 ? (realiseMoisCA / objectifMoisCA) * 100 : 0;

const tauxCumulCA =
  objectifCumulCA > 0 ? (realiseCumulCA / objectifCumulCA) * 100 : 0;

const ecartMoisCA = realiseMoisCA - objectifMoisCA;
const ecartCumulCA = realiseCumulCA - objectifCumulCA;

/* =========================
   TAUX / ECARTS / STATUTS VISITES
========================= */
const tauxMoisVisites =
  objectifMoisVisites > 0
    ? (realiseMoisVisites / objectifMoisVisites) * 100
    : 0;

const tauxCumulVisites =
  objectifCumulVisites > 0
    ? (realiseCumulVisites / objectifCumulVisites) * 100
    : 0;

const ecartMoisVisites = realiseMoisVisites - objectifMoisVisites;
const ecartCumulVisites = realiseCumulVisites - objectifCumulVisites;

function getStatutObjectif(realise, objectif) {
  if (objectif <= 0) return "Sans objectif";
  if (realise < objectif) return "Retard";
  if (realise === objectif) return "Atteint";
  return "Dépassé";
}

const statutMoisCA = getStatutObjectif(realiseMoisCA, objectifMoisCA);
const statutCumulCA = getStatutObjectif(realiseCumulCA, objectifCumulCA);

const statutMoisVisites = getStatutObjectif(
  realiseMoisVisites,
  objectifMoisVisites
);
const statutCumulVisites = getStatutObjectif(
  realiseCumulVisites,
  objectifCumulVisites
);

/* =========================
   LARGEURS BARRES VISUELLES CA
========================= */
const largeurTotaleMois = Math.max(tauxMoisCA, 100);
const largeurVertMois =
  tauxMoisCA <= 100 ? tauxMoisCA : (100 / largeurTotaleMois) * 100;
const largeurBleuMois =
  tauxMoisCA > 100 ? ((tauxMoisCA - 100) / largeurTotaleMois) * 100 : 0;

const largeurTotaleCumul = Math.max(tauxCumulCA, 100);
const largeurVertCumul =
  tauxCumulCA <= 100 ? tauxCumulCA : (100 / largeurTotaleCumul) * 100;
const largeurBleuCumul =
  tauxCumulCA > 100 ? ((tauxCumulCA - 100) / largeurTotaleCumul) * 100 : 0;

/* =========================
   LARGEURS BARRES VISUELLES VISITES
========================= */
const largeurTotaleMoisVisites = Math.max(tauxMoisVisites, 100);
const largeurVertMoisVisites =
  tauxMoisVisites <= 100
    ? tauxMoisVisites
    : (100 / largeurTotaleMoisVisites) * 100;
const largeurBleuMoisVisites =
  tauxMoisVisites > 100
    ? ((tauxMoisVisites - 100) / largeurTotaleMoisVisites) * 100
    : 0;

const largeurTotaleCumulVisites = Math.max(tauxCumulVisites, 100);
const largeurVertCumulVisites =
  tauxCumulVisites <= 100
    ? tauxCumulVisites
    : (100 / largeurTotaleCumulVisites) * 100;
const largeurBleuCumulVisites =
  tauxCumulVisites > 100
    ? ((tauxCumulVisites - 100) / largeurTotaleCumulVisites) * 100
    : 0;
/* =========================================================
   BLOC 11 - OUTILS DE SAISIE ET OPTIONS DYNAMIQUES
========================================================= */
function formatMontant(valeur) {
  return Number(valeur || 0).toFixed(2);
} 
 function chargerPiece(file) {
    if (!file) return;

    const reader = new FileReader();

    reader.onload = function (evt) {
      setPieceNom(file.name);
      setPieceType(file.type);
      setPieceData(evt.target.result);
    };

    reader.readAsDataURL(file);
  }

  function ajouterSportOption(nouveauSport) {
    const valeur = (nouveauSport || "").trim();
    if (valeur === "") return "";

    const existe = sportsOptions.some(
      (sport) => sport.toLowerCase() === valeur.toLowerCase()
    );

    if (!existe) {
      setSportsOptions(
        [...sportsOptions, valeur].sort((a, b) => a.localeCompare(b))
      );
    }

    return valeur;
  }

  function ajouterHistoriqueTypeOption(nouveauType) {
    const valeur = (nouveauType || "").trim();
    if (valeur === "") return "";

    const existe = historiqueTypeOptions.some(
      (type) => type.toLowerCase() === valeur.toLowerCase()
    );

    if (!existe) {
      setHistoriqueTypeOptions(
        [...historiqueTypeOptions, valeur].sort((a, b) => a.localeCompare(b))
      );
    }

    return valeur;
  }
/* =========================================================
   BLOC 12 - CALCULS DASHBOARD ET RECHERCHE
========================================================= */
const planningRechercheMin = planningRechercheClub.trim().toLowerCase();

const clubsFiltresPlanning = clubs.filter((club) => {
  const commercialOk =
    planningFiltreCommercial === "Tous" ||
    (club.commercial || "") === planningFiltreCommercial;

  if (!commercialOk) return false;

  const sportOk =
    planningFiltreSport === "Tous les sports" ||
    (club.sport || "") === planningFiltreSport;

  if (!sportOk) return false;

  if (planningRechercheMin === "") return true;

  const clubId = club.id || club.identifiant;

  const codeMatch = (club.code || "").toLowerCase().includes(planningRechercheMin);
  const nomMatch = (club.nom || "").toLowerCase().includes(planningRechercheMin);
  const villeMatch = (club.ville || "").toLowerCase().includes(planningRechercheMin);

  const contactsDuClub = allContacts.filter(
    (contact) => String(contact.club_id) === String(clubId)
  );

  const contactNomMatch = contactsDuClub.some((contact) =>
    (contact.nom || "").toLowerCase().includes(planningRechercheMin)
  );

  const contactTelMatch = contactsDuClub.some((contact) =>
    (contact.telephone || "").toLowerCase().includes(planningRechercheMin)
  );

  return (
    codeMatch ||
    nomMatch ||
    villeMatch ||
    contactNomMatch ||
    contactTelMatch
  );
});
/* =========================================================
   FILTRAGE CLUBS + CALCULS DASHBOARD PROPRES
========================================================= */
function getActionsDuClub(club, sourceActions = actionsFiltrees) {
  const clubId = String(club.id || club.identifiant);

  return sourceActions.filter(
    (action) => String(action.club_id) === clubId
  );
}

function calculerTotauxActions(actions) {
  const totalLivre = actions
    .filter((a) => a.type === "Livraison")
    .reduce((sum, a) => sum + (Number(a.montantTTC) || 0), 0);

  const totalFacture = actions
    .filter((a) => a.type === "Facturé")
    .reduce((sum, a) => sum + (Number(a.montantTTC) || 0), 0);

  const totalPaye = actions
    .filter((a) => a.type === "Payé")
    .reduce((sum, a) => sum + (Number(a.montantTTC) || 0), 0);

  return {
    totalLivre,
    totalFacture,
    totalPaye,
    resteAPayer: totalFacture - totalPaye
  };
}

function getSituationInterclubClub(club) {
  const documentsClub = documentsPourCodeClient(
    documentsInterclub,
    club?.numero_compte_interclub
  );
  return getSituationInterclub(documentsClub);
}

const clubsFiltres = clubsFiltresGeneraux.filter((club) => {
 const situationInterclub = getSituationInterclubClub(club);

 if (
  filtreFinancier === "Facturé non payé" &&
  !situationInterclub.nonPaye
) {
  return false;
}
  return true;
});

const idsClubsFiltres = clubsFiltres.map((club) =>
  String(club.id || club.identifiant)
);

const actionsHistoriquesDashboard = actionsFiltrees.filter((action) =>
  idsClubsFiltres.includes(String(action.club_id))
);
const actionsPlanningDashboard = planning.filter((action) =>
  idsClubsFiltres.includes(String(action.club_id))
);

const totalClubs = clubsFiltres.length;

const {
  totalLivre: totalLivraisonsTTC,
  totalFacture: totalFacturesTTC,
  totalPaye: totalPayesTTC,
  resteAPayer: soldeRestantEncaisser
} = calculerTotauxActions(actionsHistoriquesDashboard);
/* =========================================================
  BLOC 13 - DOUBLONS ET GESTION MAGASINS
========================================================= */
  function detecterDoublonClub(nomSaisi) {
    const nom = (nomSaisi || "").trim().toLowerCase();
    if (nom === "") return "";

    const clubsTrouves = clubs.filter((club) => {
      const nomClub = (club.nom || "").trim().toLowerCase();
      return nomClub.includes(nom) || nom.includes(nomClub);
    });

    if (clubsTrouves.length === 0) return "";

    return (
      "Risque de doublon club : " +
      clubsTrouves.map((c) => `${c.code} - ${c.nom}`).join(" / ")
    );
  }

  function detecterDoublonContact(telephoneSaisi, mailSaisi) {
    const tel = (telephoneSaisi || "").trim().toLowerCase();
    const mail = (mailSaisi || "").trim().toLowerCase();

    if (tel === "" && mail === "") return "";

    const trouves = [];

    allContacts.forEach((contact) => {
      const telContact = (contact.telephone || "").trim().toLowerCase();
      const mailContact = (contact.mail || "").trim().toLowerCase();

      const telMatch = tel !== "" && telContact !== "" && tel === telContact;
      const mailMatch = mail !== "" && mailContact !== "" && mail === mailContact;

      if (telMatch || mailMatch) {
        const club = clubs.find(
          (c) => String(c.id || c.identifiant) === String(contact.club_id)
        );

        trouves.push(
          `${club?.code || "?"} - ${club?.nom || "Club inconnu"} - ${
            contact.nom || "Sans nom"
          }`
        );
      }
    });

    if (trouves.length === 0) return "";

    return "Risque de doublon contact : " + trouves.join(" / ");
  }

  function totalMagasins(magasins) {
    return (magasins || []).reduce((total, mag) => {
      return total + (Number(mag.taux) || 0);
    }, 0);
  }

  function updateMagasinCreation(index, field, value) {
    const copie = [...magasinsCreation];
    copie[index] = { ...copie[index], [field]: value };
    setMagasinsCreation(copie);
  }

  function addMagasinCreation() {
    setMagasinsCreation([...magasinsCreation, { nom: "", taux: "0" }]);
  }

  function removeMagasinCreation(index) {
    const ok = window.confirm("Voulez-vous vraiment supprimer ce magasin ?");
    if (!ok) return;

    const copie = magasinsCreation.filter((_, i) => i !== index);
    setMagasinsCreation(copie.length > 0 ? copie : [{ nom: "", taux: "0" }]);
  }

  function updateMagasinDetail(index, field, value) {
    const copie = [...magasinsClub];
    copie[index] = { ...copie[index], [field]: value };
    setMagasinsClub(copie);
  }

  function addMagasinDetail() {
    setMagasinsClub([...magasinsClub, { nom: "", taux: "0", adresse: "", code_postal: "", ville: "", telephone: "", email: "", actif: true }]);
  }

  function removeMagasinDetail(index) {
    const ok = window.confirm("Voulez-vous vraiment supprimer ce magasin ?");
    if (!ok) return;

    const magasinRetire = magasinsClub[index];
    if (magasinRetire?.id) setMagasinsClubSupprimes((ids) => [...new Set([...ids, magasinRetire.id])]);
    const copie = magasinsClub.filter((_, i) => i !== index);
    setMagasinsClub(copie.length > 0 ? copie : []);
  }
/* =========================================================
  BLOC 14 - GOOGLE AGENDA ET EXPORT CSV
========================================================= */	
function formaterDateGoogle(dateStr, heureStr) {
  if (!dateStr) return "";

  if (!heureStr) {
    const debut = new Date(`${dateStr}T00:00:00`);
    const fin = new Date(debut);
    fin.setDate(fin.getDate() + 1);

    const y1 = debut.getFullYear();
    const m1 = String(debut.getMonth() + 1).padStart(2, "0");
    const d1 = String(debut.getDate()).padStart(2, "0");

    const y2 = fin.getFullYear();
    const m2 = String(fin.getMonth() + 1).padStart(2, "0");
    const d2 = String(fin.getDate()).padStart(2, "0");

    return `${y1}${m1}${d1}/${y2}${m2}${d2}`;
  }

  const debut = new Date(`${dateStr}T${heureStr}:00`);
  const y = debut.getFullYear();
  const m = String(debut.getMonth() + 1).padStart(2, "0");
  const d = String(debut.getDate()).padStart(2, "0");
  const h = String(debut.getHours()).padStart(2, "0");
  const min = String(debut.getMinutes()).padStart(2, "0");

  return `${y}${m}${d}T${h}${min}00`;
}

function calculerFinPlanning(dateStr, heureStr, dureeStr) {
  if (!dateStr) return "";

  if (dureeStr === "journee") {
    const debut = new Date(`${dateStr}T00:00:00`);
    const fin = new Date(debut);
    fin.setDate(fin.getDate() + 1);

    const y = fin.getFullYear();
    const m = String(fin.getMonth() + 1).padStart(2, "0");
    const d = String(fin.getDate()).padStart(2, "0");

    return `${y}${m}${d}`;
  }

  const debut = new Date(`${dateStr}T${heureStr || "10:00"}:00`);
  const fin = new Date(debut);

  if (dureeStr === "30min") fin.setMinutes(fin.getMinutes() + 30);
  else if (dureeStr === "1h") fin.setHours(fin.getHours() + 1);
  else if (dureeStr === "2h") fin.setHours(fin.getHours() + 2);
  else if (dureeStr === "3h") fin.setHours(fin.getHours() + 3);
  else if (dureeStr === "demi-journee") fin.setHours(fin.getHours() + 4);
  else fin.setHours(fin.getHours() + 2);

  const y = fin.getFullYear();
  const m = String(fin.getMonth() + 1).padStart(2, "0");
  const d = String(fin.getDate()).padStart(2, "0");
  const h = String(fin.getHours()).padStart(2, "0");
  const min = String(fin.getMinutes()).padStart(2, "0");

  return `${y}${m}${d}T${h}${min}00`;
}

function ouvrirGoogleAgendaDepuisPlanning(ligne) {
  const titre = `${ligne.type || "Rendez-vous"} - ${ligne.clubCode || ""} ${ligne.clubNom || ""}`.trim();

  let dates = "";

  if (ligne.duree_rdv === "journee") {
    const debutJournee = formaterDateGoogle(ligne.date_rdv, null);
    const finJournee = calculerFinPlanning(ligne.date_rdv, null, "journee");
    dates = `${debutJournee}${finJournee ? finJournee : ""}`;
  } else {
    const debut = formaterDateGoogle(ligne.date_rdv, ligne.heure_rdv || "10:00");
    const fin = calculerFinPlanning(
      ligne.date_rdv,
      ligne.heure_rdv || "10:00",
      ligne.duree_rdv || "1h"
    );
    dates = `${debut}/${fin}`;
  }

  const details = [
    `Club : ${ligne.clubCode || ""} ${ligne.clubNom || ""}`.trim(),
    `Type : ${ligne.type || ""}`,
    `Commercial : ${ligne.commercial || ""}`,
    `Commentaire : ${ligne.commentaire || ""}`
  ].join("\n");

  const url =
    "https://calendar.google.com/calendar/render?action=TEMPLATE" +
    `&text=${encodeURIComponent(titre)}` +
    `&dates=${encodeURIComponent(dates)}` +
    `&details=${encodeURIComponent(details)}`;

  window.open(url, "_blank");
}

  function nettoyerCSV(valeur) {
    const texte = String(valeur ?? "");
    return `"${texte.replace(/"/g, '""')}"`;
  }

  function exportClubsCSV() {
    const entetes = [
      "Code club",
      "Nom du club",
      "Ville",
      "Sport pratiqué",
      "Adherents",
      "Potentiel TTC",
      "Commercial",
      "Contacts",
      "Nb contacts",
      "Nb actions"
    ];

    const lignes = clubsFiltres.map((club) => {
      const contactsDuClub = allContacts.filter(
        (c) => String(c.club_id) === String(club.id || club.identifiant)
      );

      const actionsDuClub = toutesActions.filter(
        (a) => String(a.club_id) === String(club.id || club.identifiant)
      );

      const contactsTexte = contactsDuClub
        .map((c) => `${c.nom || ""} / ${c.telephone || ""} / ${c.mail || ""}`)
        .join(" | ");

      return [
        club.code || "",
        club.nom || "",
        club.ville || "",
        club.sport || "",
        club.adherents || "",
        club.potentiel || "",
        club.commercial || "",
        contactsTexte,
        contactsDuClub.length,
        actionsDuClub.length
      ];
    });

    const contenu = [
      entetes.map(nettoyerCSV).join(";"),
      ...lignes.map((ligne) => ligne.map(nettoyerCSV).join(";"))
    ].join("\n");

    const blob = new Blob(["\uFEFF" + contenu], {
      type: "text/csv;charset=utf-8;"
    });

    const url = URL.createObjectURL(blob);
    const lien = document.createElement("a");
    const dateTexte = new Date().toISOString().slice(0, 10);

    lien.href = url;
    lien.download = `crm-clubs-${dateTexte}.csv`;
    document.body.appendChild(lien);
    lien.click();
    document.body.removeChild(lien);
    URL.revokeObjectURL(url);
  }

/* =========================================================
   BLOC 14A - CHARGEMENT AUTH
========================================================= */
if (demandeGroupeToken) {
  return (
    <QuestionnaireDemandeGroupe
      clientSupabase={supabase}
      token={demandeGroupeToken}
    />
  );
}

if (portailClubCampagneId) {
  return <PortailClub clientSupabase={supabase} campagneId={portailClubCampagneId} groupeSlug={groupeSelectionne} />;
}

if (rendezVousPublicToken) {
  return <RendezVousPublic client={supabase} token={rendezVousPublicToken} />;
}

if (boutiqueClubPublicActif) {
  return <BoutiqueClubPublic clientSupabase={supabase} groupeSlug={boutiqueClubPublicGroupe} campagneInitiale={boutiqueClubPublicCampagne} packInitial={boutiqueClubPublicPack} documentInitial={boutiqueClubPublicDocument} routeInitiale={routeBoutiqueClubActive ? routeBoutiqueClub : null} />;
}

if (devisPublicToken) {
  return <PageDevisClient clientSupabase={supabase} token={devisPublicToken} />;
}

if (contratPublicToken) {
  return <PageContratClient clientSupabase={supabase} token={contratPublicToken} />;
}

if (batPublicToken) {
  return <PageBatClient clientSupabase={supabase} token={batPublicToken} />;
}

if (demandeUtilisateursToken) {
  return <QuestionnaireDemandeUtilisateurs clientSupabase={supabase} token={demandeUtilisateursToken} />;
}

if (activationUtilisateurToken) {
  return <ActivationUtilisateur clientSupabase={supabase} token={activationUtilisateurToken} />;
}

if (modeReinitialisationMotDePasse) {
  return (
    <div style={{ height: "100vh", display: "flex", justifyContent: "center", alignItems: "center", background: "#f2f2f2" }}>
      <div style={{ background: "white", padding: 30, borderRadius: 10, width: 300, textAlign: "center" }}>
        <h2>Nouveau mot de passe</h2>
        <input
          type="password"
          placeholder="Nouveau mot de passe"
          value={nouveauMotDePasse}
          onChange={(e) => setNouveauMotDePasse(e.target.value)}
          style={{ width: "100%", padding: 10, marginTop: 10 }}
        />
        <input
          type="password"
          placeholder="Confirmer le nouveau mot de passe"
          value={confirmationNouveauMotDePasse}
          onChange={(e) => setConfirmationNouveauMotDePasse(e.target.value)}
          style={{ width: "100%", padding: 10, marginTop: 10 }}
        />
        <button
          onClick={handleModifierMotDePasse}
          disabled={!supabaseClientActif}
          style={{ marginTop: 20, width: "100%", padding: 12 }}
        >
          VALIDER
        </button>
      </div>
    </div>
  );
}

if (groupeErreur) {
  return (
    <div role="alert" style={{ minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center", background: "#f2f2f2", fontFamily: "Arial", padding: 24 }}>
      <div style={{ maxWidth: 720, background: "white", border: "1px solid #d92d20", borderRadius: 10, padding: 24 }}>
        <h2>Accès au groupe impossible</h2>
        <p>{groupeErreur}</p>
        <p>Groupe demandé : <strong>{groupeSelectionne}</strong></p>
        <button
          type="button"
          onClick={() => {
            setGroupeErreur("");
            setReessaiInitialisation((valeur) => valeur + 1);
          }}
          style={{ padding: "10px 16px", fontWeight: 700, cursor: "pointer" }}
        >
          RÉESSAYER
        </button>
      </div>
    </div>
  );
}

if (authLoading) {
  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "#f2f2f2",
        fontFamily: "Arial"
      }}
    >
      Chargement...
    </div>
  );
}
// Compatibilité avec les anciens favoris et raccourcis : l'URL historique
// ouvre désormais le circuit authentifié actuel, jamais l'ancien écran PIN.
if (enlevementBoutiqueClub && userConnected && ["vendeur_magasin", "commercial", "superviseur"].includes(userConnected.role) && supabaseClientActif) {
  return (
    <BoutiqueClubRetraitVendeur
      clientSupabase={supabaseClientActif}
      utilisateur={userConnected}
      onLogout={handleLogout}
    />
  );
}
// Le vendeur magasin authentifié arrive directement dans le même circuit de
// retrait que les commerciaux et superviseurs. Les RPC restent l'autorité.
if (userConnected?.role === "vendeur_magasin" && supabaseClientActif) {
  return (
    <BoutiqueClubRetraitVendeur
      clientSupabase={supabaseClientActif}
      utilisateur={userConnected}
      onLogout={handleLogout}
    />
  );
}
/* =========================================================
   BLOC 14B - ECRAN CLIENTS INTERCLUB
========================================================= */
if (userConnected && screen === "interclub") {
  return (
    <ClientsInterclubAValider
  userConnected={userConnected}
  setPage={setScreen}
  codeClientInitial={codeClientInterclubInitial}
/>
  );
}
if (userConnected && screen === "syntheseInterclub") {
  return (
<SyntheseInterclub
  userConnected={userConnected}
  setPage={setScreen}
/>
  );
}
if (userConnected?.role === "superviseur" && screen === "administrationGroupes") {
  return (
    <AdministrationGroupes
      clientSupabase={supabaseClientActif}
      userConnected={userConnected}
      retourDashboard={() => setScreen("dashboard")}
    />
  );
}
if (BOUTIQUECLUB_ENABLED && userConnected && ["superviseur", "gestionnaire", "commercial"].includes(userConnected.role) && screen === "boutiqueClub" && supabaseClientActif) {
  return (
    <BoutiqueClub
      clientSupabase={supabaseClientActif}
      groupe={groupeSelectionne}
      clubs={clubs}
      contacts={allContacts}
      utilisateur={userConnected}
      clubInitial={boutiqueClubClubInitial}
      destinationInitiale={boutiqueClubDestinationInitiale}
      onOuvrirDevis={async (clubCible, id) => { await chargerDetailClub(clubCible); setDevisInitialId(id); setBoutiqueClubClubInitial(null); setScreen("devisCRM"); }}
      onGererContacts={async (clubCible) => { await chargerDetailClub(clubCible); setBoutiqueClubClubInitial(null); setScreen('detailClub'); setTimeout(()=>document.getElementById('section-contacts')?.scrollIntoView({behavior:'smooth'}),0); }}
      retourDashboard={() => { setBoutiqueClubClubInitial(null); setBoutiqueClubDestinationInitiale(null); setScreen(clubSelected ? "detailClub" : "dashboard"); }}
    />
  );
}
if (BOUTIQUECLUB_ENABLED && userConnected && ["superviseur", "commercial"].includes(userConnected.role) && screen === "enlevementBoutiqueClub" && supabaseClientActif) {
  return (
    <BoutiqueClubRetraitVendeur
      clientSupabase={supabaseClientActif}
      utilisateur={userConnected}
      onLogout={() => setScreen("dashboard")}
    />
  );
}
if (userConnected && screen === "gestionPrimes") {
  return <GestionPrimes utilisateur={userConnected} groupe={groupeSelectionne} clientSupabase={supabaseClientActif} contexte={contextePrime} onRetour={() => setScreen("dashboard")} onCreerAction={async (action) => {
    const cible = (clubs || []).find((club) => String(club.id || club.identifiant) === String(action.clubId) || String(club.code || '') === String(action.clubCode || '')) || {};
    const ligne = {
      jour: new Date(`${action.dateCible}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'long' }).replace(/^./, (c) => c.toUpperCase()),
      date_rdv: action.dateCible, heure_rdv: '09:00', duree_rdv: '30min', type: action.type || 'Relance',
      commercial: action.commercial || contextePrime?.commercial || userConnected?.nom || '', club_id: cible.id || cible.identifiant || null,
      clubCode: cible.code || action.clubCode || '', clubNom: cible.nom || action.clubNom || '', groupe: groupeSelectionne,
      commentaire: encoderCommentaireAction([action.action, action.description].filter(Boolean).join(' — ') || action.type || 'Action de gestion', { objet: action.action || action.type, reference: action.reference || '', montant: Number(action.montant || 0), montant_document_d2: Number(action.montant || 0), type_origine: action.typeOrigine || null, date_document: action.dateDocument || null, element_lie: action.reference || null, statut: String(action.statut || 'À FAIRE').toLocaleLowerCase('fr-FR').replaceAll(' ', '_'), priorite: action.priorite || 'Normale', responsable: action.responsable || action.commercial || contextePrime?.commercial || '', mode_date: action.modeDate || 'A_FAIRE_AVANT_LE', origine: 'GESTION PRIMES', createur: userConnected?.nom || '', date_creation: new Date().toISOString() })
    };
    const { data, error } = await supabaseClientActif.from('planning').insert([ligne]).select().single();
    if (error) throw error;
    setPlanning((actuel) => [data, ...actuel]);
    setContextePrime((courant) => ({ ...courant, planActions: [data, ...(courant?.planActions || [])] }));
    return data;
  }} onModifierAction={async (id, changements) => {
    const { data, error } = await supabaseClientActif.from('planning').update(changements).eq('id', id).eq('groupe', groupeSelectionne).select().single();
    if (error) throw error;
    setPlanning((actuel) => actuel.map((item) => item.id === id ? data : item));
    setContextePrime((courant) => ({ ...courant, planActions: (courant?.planActions || []).map((item) => item.id === id ? data : item) }));
    return data;
  }} />;
}
if (userConnected && screen === "kilometrage") {
  return <KilometrageCRM actions={planning} utilisateur={userConnected} groupe={groupeSelectionne} onRetour={() => setScreen("planning")} />;
}
/* =========================================================
  BLOC 15 - RENDU ECRAN PLANNING
========================================================= */

if (userConnected && screen === "planning" && supabaseClientActif) {
  return (
    <PlanningCRM
      actions={planning}
      clubs={clubs}
      contacts={allContacts}
      utilisateur={userConnected}
      groupe={groupeSelectionne}
      vehicules={(() => { try { return JSON.parse(localStorage.getItem(`crm-clubs:${groupeSelectionne}:vehicules-local`)) || []; } catch { return []; } })()}
      relevesKilometriques={(() => { try { return JSON.parse(localStorage.getItem(`crm-clubs:${groupeSelectionne}:releves-km-local`)) || []; } catch { return []; } })()}
      propositionInitiale={propositionRdvInitiale}
      onPropositionConsommee={() => setPropositionRdvInitiale(null)}
      onCreerDemande={(donnees) => creerDemandeRdv(supabaseClientActif, donnees)}
      onAnnulerDemande={(id, raison) => annulerDemandeRdv(supabaseClientActif, id, raison)}
      onRetour={() => setScreen("dashboard")}
      onKilometrage={() => setScreen("kilometrage")}
      onOuvrirClub={async (clubId) => {
        const club = clubs.find((item) => String(item.id || item.identifiant) === String(clubId));
        if (!club) return;
        await chargerDetailClub(club);
        setScreen("detailClub");
      }}
      onCreer={async (nouvelleLigne) => {
        const { data, error } = await supabaseClientActif
          .from("planning")
          .insert([{ ...nouvelleLigne, groupe: groupeSelectionne }])
          .select()
          .single();
        if (error) throw error;
        setPlanning((actuel) => [data, ...actuel]);
        return data;
      }}
      onModifier={async (id, changements) => {
        const { data, error } = await supabaseClientActif
          .from("planning")
          .update(changements)
          .eq("id", id)
          .eq("groupe", groupeSelectionne)
          .select()
          .single();
        if (error) throw error;
        setPlanning((actuel) => actuel.map((item) => item.id === id ? data : item));
        setContextePrime((courant) => ({ ...courant, planActions: (courant?.planActions || []).map((item) => item.id === id ? data : item) }));
        return data;
      }}
      onQualifier={async (action, qualification) => {
        const existante = Boolean(action?.commentaire && action.commentaire.includes('date_qualification'));
        const { data, error } = await supabaseClientActif.rpc("crm_visite_qualifier", {
          p_groupe: groupeSelectionne, p_planning_id: action.id, p_date_qualification: qualification.date,
          p_mode: "MANUEL", p_motif: qualification.motif, p_preuve: qualification.preuve || null,
          p_action_liee_id: null, p_motif_rectification: existante ? qualification.rectification : null
        });
        if (error) throw error;
        return data;
      }}
    />
  );
}

if (userConnected && screen === "planning") {

  const planningCommercial =
    commercialActif === "Toute l'équipe"
      ? planning
      : planning.filter((ligne) => ligne.commercial === commercialActif);

  const planningUtilisateur = planningCommercial.filter(isPlanningDansPeriode);

  return (
    <div style={stylePage}>
<div style={{ marginBottom: 15, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
  <div>
    <button
      onClick={() => setScreen("dashboard")}
      style={{
        padding: "8px 15px",
        borderRadius: 8,
        border: "none",
        background: "#333",
        color: "white",
        cursor: "pointer"
      }}
    >
      ← Retour dashboard
    </button>

    <button
      onClick={() => setScreen("interclub")}
      style={{
        padding: "8px 15px",
        borderRadius: 8,
        border: "none",
        background: "#1565c0",
        color: "white",
        cursor: "pointer",
        marginLeft: 10
      }}
    >
      Clients Interclub
    </button>
  </div>

  <h2 style={{ margin: 0 }}>Planning semaine</h2>
</div>     

      {/* ================= FILTRES HAUT = AFFICHAGE DU PLANNING ================= */}
      <div
        style={{
          background: "white",
          padding: 15,
          borderRadius: 10,
          marginBottom: 20,
          position: "sticky",
          top: 10,
          zIndex: 1000,
          boxShadow: "0 4px 12px rgba(0,0,0,0.12)"
        }}
      >
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>

          {/* Filtre commercial affichage planning */}
          <div>
            <div style={{ fontWeight: "bold", marginBottom: 5 }}>Filtre commercial</div>
            <select
              style={{ padding: 10, minWidth: 180 }}
              value={filtreCommercial}
              onChange={(e) => setFiltreCommercial(e.target.value)}
            >
              <option>Moi</option>
              <option>Adrien</option>
              <option>Hervé</option>
              <option>Bruno</option>
              <option>Non attribué</option>
              <option>Toute l'équipe</option>
            </select>
          </div>

          {/* Période affichage planning */}
          <div>
            <div style={{ fontWeight: "bold", marginBottom: 5 }}>Période</div>
            <select
              style={{ padding: 10, minWidth: 220 }}
              value={filtrePlanningPeriode}
              onChange={(e) => setFiltrePlanningPeriode(e.target.value)}
            >
              <option>Semaine en cours</option>
              <option>Semaine précédente</option>
              <option>Semaine suivante</option>
              <option>Mois en cours</option>
              <option>Mois précédent</option>
              <option>Mois suivant</option>
              <option>Date du mois</option>
            </select>
          </div>

          {filtrePlanningPeriode === "Date du mois" && (
            <div>
              <div style={{ fontWeight: "bold", marginBottom: 5 }}>Mois</div>
              <input
                type="month"
                style={{ padding: 10, minWidth: 180 }}
                value={planningMoisFiltre}
                onChange={(e) => setPlanningMoisFiltre(e.target.value)}
              />
            </div>
          )}

          <div style={{ marginLeft: "auto", display: "flex", alignItems: "flex-end" }}>
            <button
              style={{
                padding: 10,
                background: "#2e7d32",
                color: "white",
                border: "none",
                borderRadius: 6
              }}
              onClick={() => setScreen("club")}
            >
              + Nouveau club
            </button>
          </div>
        </div>
      </div>

      {/* ================= LISTE PLANNING ================= */}
      {planningUtilisateur.map((ligne) => (
        <div
          key={ligne.id}
          style={{
            background: "white",
            borderRadius: 10,
            padding: 12,
            marginBottom: 10
          }}
        >
          <div><strong>{ligne.jour}</strong></div>
          <div>
            {ligne.date_rdv || ""}{" "}
            {ligne.heure_rdv ? `- ${ligne.heure_rdv}` : ""}
            {ligne.duree_rdv ? ` - ${ligne.duree_rdv}` : ""}
          </div>
          <div>{ligne.type}</div>
          <div>{ligne.clubCode} - {ligne.clubNom}</div>
          <div>{ligne.commentaire}</div>
          <div><em>{ligne.commercial}</em></div>

          <button
            style={{
              padding: 8,
              marginTop: 10,
              marginRight: 10,
              background: "#1565c0",
              color: "white",
              border: "none",
              borderRadius: 6
            }}
            onClick={() => ouvrirGoogleAgendaDepuisPlanning(ligne)}
          >
            Google Agenda
          </button>

          <button
            style={{
              padding: 8,
              marginTop: 10,
              background: "#b71c1c",
              color: "white",
              border: "none",
              borderRadius: 6
            }}
            onClick={async () => {
              const ok = window.confirm(`Annuler cet élément du planning ?\n\nClub : ${ligne.clubNom || ligne.clubCode || "—"}\nType : ${ligne.type || "—"}\nDate : ${ligne.date_rdv || "—"}\nResponsable : ${ligne.commercial || "—"}`);
              if (!ok) return;
              const motif = window.prompt("Motif d’annulation (facultatif)", "") || "";

              if (!supabaseClientActif) return;

const { error } = await supabaseClientActif
  .from("planning")
  .update({ commentaire: encoderCommentaireAction(ligne.commentaire || "", { statut: "annule", annule_le: new Date().toISOString(), annule_par: userConnected?.nom || "Utilisateur", motif_annulation: motif.trim() || null }) })
  .eq("id", ligne.id);

if (error) {
  console.log("ERREUR annulation planning :", error);
  return;
}
              setPlanning((prev) => prev.map((p) => p.id === ligne.id ? { ...p, commentaire: encoderCommentaireAction(p.commentaire || "", { statut: "annule", annule_le: new Date().toISOString(), annule_par: userConnected?.nom || "Utilisateur", motif_annulation: motif.trim() || null }) } : p));
            }}
          >
            Annuler
          </button>
        </div>
      ))}

      {/* ================= AJOUT ================= */}
      <div style={{ background: "white", padding: 15, borderRadius: 10 }}>

        <h4>Ajouter au planning</h4>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div>
            <div style={{ fontWeight: "bold", marginBottom: 5 }}>Jour</div>
            <select
              style={{ padding: 10, width: 160 }}
              value={planningJour}
              onChange={(e) => setPlanningJour(e.target.value)}
            >
              <option>Lundi</option>
              <option>Mardi</option>
              <option>Mercredi</option>
              <option>Jeudi</option>
              <option>Vendredi</option>
              <option>Samedi</option>
            </select>
          </div>

          <div>
            <div style={{ fontWeight: "bold", marginBottom: 5 }}>Date</div>
            <input
              type="date"
              style={{ padding: 10, width: 170 }}
              value={planningDate}
              onChange={(e) => setPlanningDate(e.target.value)}
            />
          </div>

          <div>
            <div style={{ fontWeight: "bold", marginBottom: 5 }}>Heure</div>
            <input
              type="time"
              style={{ padding: 10, width: 140 }}
              value={planningHeure}
              onChange={(e) => setPlanningHeure(e.target.value)}
              disabled={planningDuree === "journee"}
            />
          </div>

          <div>
            <div style={{ fontWeight: "bold", marginBottom: 5 }}>Durée</div>
            <select
              style={{ padding: 10, width: 170 }}
              value={planningDuree}
              onChange={(e) => setPlanningDuree(e.target.value)}
            >
              <option value="30min">30 min</option>
              <option value="1h">1 h</option>
              <option value="2h">2 h</option>
              <option value="3h">3 h</option>
              <option value="demi-journee">1/2 journée</option>
              <option value="journee">Journée</option>
            </select>
          </div>

          <div>
            <div style={{ fontWeight: "bold", marginBottom: 5 }}>Type</div>
            <select
              style={{ padding: 10, width: 180 }}
              value={planningType}
              onChange={(e) => setPlanningType(e.target.value)}
            >
              <option>Visite club</option>
              <option>Prospection</option>
              <option>Offre</option>
              <option>Relance</option>
              <option>Commande</option>
              <option>Livraison</option>
              <option>Animation club</option>
              <option>Soirée privée</option>
              <option>Suivi</option>
            </select>
          </div>
        </div>

        {/* ================= FILTRES CLUB POUR LA SAISIE ================= */}
        <div
          style={{
            marginTop: 20,
            padding: 12,
            background: "#f7f7f7",
            borderRadius: 8
          }}
        >
          <div style={{ fontWeight: "bold", marginBottom: 10 }}>
            Filtres pour choisir le club
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div>
              <div style={{ fontWeight: "bold", marginBottom: 5 }}>Club / commercial</div>
              <select
                style={{ padding: 10, minWidth: 180 }}
                value={planningFiltreCommercial}
                onChange={(e) => setPlanningFiltreCommercial(e.target.value)}
              >
                <option value="Tous">Tous</option>
                <option>Adrien</option>
                <option>Hervé</option>
                <option>Bruno</option>
                <option>Non attribué</option>
              </select>
            </div>

            <div>
              <div style={{ fontWeight: "bold", marginBottom: 5 }}>Sport</div>
              <select
                style={{ padding: 10, minWidth: 180 }}
                value={planningFiltreSport}
                onChange={(e) => setPlanningFiltreSport(e.target.value)}
              >
                <option>Tous les sports</option>
                {sportsOptions.map((sport) => (
                  <option key={sport} value={sport}>{sport}</option>
                ))}
              </select>
            </div>

            <div style={{ flex: 1, minWidth: 280 }}>
              <div style={{ fontWeight: "bold", marginBottom: 5 }}>Recherche club</div>
              <input
                style={{ padding: 10, width: "100%" }}
                placeholder="Code, nom, ville, contact, téléphone"
                value={planningRechercheClub}
                onChange={(e) => setPlanningRechercheClub(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div style={{ marginTop: 15 }}>
          <div style={{ fontWeight: "bold", marginBottom: 5 }}>Club</div>
          <select
            style={{ padding: 10, width: "100%", maxWidth: 500 }}
            value={clubSelected ? clubSelected.code : ""}
            onChange={(e) => {
              const clubTrouve = clubs.find((club) => club.code === e.target.value);
              setClubSelected(clubTrouve || null);
            }}
          >
            <option value="">Choisir un club</option>

            {clubsFiltresPlanning.map((club) => (
              <option key={club.code} value={club.code}>
                {club.code} - {club.nom} ({club.ville})
              </option>
            ))}
          </select>
        </div>

        {clubsFiltresPlanning.length === 0 && (
          <div style={{ color: "#b71c1c", fontWeight: "bold", marginTop: 10 }}>
            Aucun club trouvé avec ces filtres.
          </div>
        )}

        {clubsFiltresPlanning.length === 0 && (
          <button
            style={{
              marginTop: 10,
              padding: 10,
              background: "#2e7d32",
              color: "white",
              border: "none",
              borderRadius: 6
            }}
            onClick={() => setScreen("club")}
          >
            + Créer un club
          </button>
        )}

        <div style={{ marginTop: 15 }}>
          <div style={{ fontWeight: "bold", marginBottom: 5 }}>Commentaire</div>
          <textarea
            style={{
              padding: 10,
              width: "100%",
              maxWidth: 500,
              minHeight: 90,
              resize: "vertical"
            }}
            placeholder="Commentaire"
            value={planningCommentaire}
            onChange={(e) => setPlanningCommentaire(e.target.value)}
          />
        </div>

        <div style={{ marginTop: 15, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            style={{
              padding: 10,
              background: "#1565c0",
              color: "white",
              border: "none",
              borderRadius: 6
            }}
            onClick={async () => {
              if (!clubSelected) {
                alert("Merci de choisir un club");
                return;
              }

              const nouvelleLigne = {
                jour: planningJour,
                date_rdv: planningDate,
                heure_rdv: planningDuree === "journee" ? "" : planningHeure,
                duree_rdv: planningDuree,
                type: planningType,
                commentaire: planningCommentaire,
                club_id: clubSelected.id || clubSelected.identifiant,
                clubCode: clubSelected.code || "",
                clubNom: clubSelected.nom || "",
                commercial: userConnected?.nom || ""
              };

              const { data, error } = await supabase
                .from("planning")
                .insert([nouvelleLigne])
                .select()
                .single();

              if (error) {
                console.log("ERREUR INSERT PLANNING :", error);
                alert("Erreur lors de l'enregistrement du planning");
                return;
              }

              setPlanning((prev) => [data, ...prev]);

              setPlanningJour("Lundi");
              setPlanningDate(getDateJourPlanning("Lundi"));
              setPlanningHeure("10:00");
              setPlanningDuree("1h");
              setPlanningType("Visite club");
              setPlanningCommentaire("");
              setClubSelected(null);

              /* remise à zéro des filtres de choix club */
              setPlanningFiltreCommercial("Tous");
              setPlanningFiltreSport("Tous les sports");
              setPlanningRechercheClub("");
            }}
          >
            Ajouter au planning
          </button>

          {clubSelected && (
            <button
              style={{
                padding: 10,
                background: "#2e7d32",
                color: "white",
                border: "none",
                borderRadius: 6
              }}
              onClick={() => {
                const ligneTemp = {
                  type: planningType,
                  clubCode: clubSelected.code || "",
                  clubNom: clubSelected.nom || "",
                  commercial: userConnected?.nom || "",
                  commentaire: planningCommentaire,
                  date_rdv: planningDate,
                  heure_rdv: planningDuree === "journee" ? "" : planningHeure,
                  duree_rdv: planningDuree,
                  jour: planningJour
                };

                ouvrirGoogleAgendaDepuisPlanning(ligneTemp);
              }}
            >
              Préparer Google Agenda
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
if (userConnected && screen === "devisGeneral" && supabaseClientActif) {
  return <DashboardDevis modePage clientSupabase={supabaseClientActif} groupe={groupeSelectionne} clubs={clubs} clubsFiltres={clubs}
    onRetour={() => setScreen("dashboard")}
    onNouveau={async (clubId) => { const club = clubs.find((item) => String(item.id || item.identifiant) === String(clubId)); if (!club) return; await chargerDetailClub(club); setDevisInitialId(null); setDevisInterclubInitialId(null); setDevisDepuisGeneral(true); setScreen("devisCRMNouveau"); }}
    onOuvrir={async (devis) => { const club = clubs.find((item) => String(item.id || item.identifiant) === String(devis.club_id)); if (!club) return; await chargerDetailClub(club); setDevisInitialId(devis.origine === 'CRM' ? devis.id : null); setDevisInterclubInitialId(devis.origine === 'CRM' ? null : devis.id); setDevisDepuisGeneral(true); setScreen("devisCRM"); }}/>
}
if (userConnected && screen === "annuaireClubs") {
  return <AnnuaireClubs
    clubs={clubs}
    documentsInterclub={documentsInterclub}
    groupe={groupeSelectionne}
    version={VERSION}
    onRetour={() => setScreen("dashboard")}
    onOuvrir={async (club) => {
      setAfficherToutesActionsClub(false);
      setScreenRetourClub("annuaireClubs");
      await chargerDetailClub(club);
      setScreen("detailClub");
    }}
  />;
}
if (userConnected && (screen === "devisCRM" || screen === "devisCRMNouveau") && clubSelected && supabaseClientActif) {
  return (
    <DevisCRM
      supabaseClient={supabaseClientActif}
      groupe={groupeSelectionne}
      club={clubSelected}
      clubs={clubs}
      contacts={contactsClub}
      piecesClub={piecesClub}
      utilisateur={userConnected}
      devisInitialId={devisInitialId}
      interclubInitialId={devisInterclubInitialId}
      demarrerEnCreation={screen === "devisCRMNouveau"}
      onContactEnregistre={(contact) => {
        setContactsClub((actuels) => actuels.some((item) => item.id === contact.id) ? actuels.map((item) => item.id === contact.id ? contact : item) : [...actuels, contact]);
        setAllContacts((actuels) => actuels.some((item) => item.id === contact.id) ? actuels.map((item) => item.id === contact.id ? contact : item) : [...actuels, contact]);
      }}
      onOuvrirDevisClub={async (clubCible, id) => { await chargerDetailClub(clubCible); setDevisInitialId(id); setScreen("devisCRM"); }}
      onRetour={() => { setDevisInitialId(null); setDevisInterclubInitialId(null); setScreen(devisDepuisGeneral ? "devisGeneral" : "detailClub"); }}
    />
  );
}
if (userConnected && screen === "contratsCRM" && clubSelected && supabaseClientActif) {
  return <ContratsCRM supabaseClient={supabaseClientActif} groupe={groupeSelectionne} club={clubSelected} contacts={contactsClub} piecesClub={piecesClub} utilisateur={userConnected} contratInitialId={contratInitialId} renouvelerInitialId={contratRenouvelerId} onContactEnregistre={(contact)=>{setContactsClub(a=>a.some(x=>x.id===contact.id)?a.map(x=>x.id===contact.id?contact:x):[...a,contact]);setAllContacts(a=>a.some(x=>x.id===contact.id)?a.map(x=>x.id===contact.id?contact:x):[...a,contact])}} onRetour={()=>{setContratInitialId(null);setContratRenouvelerId(null);setScreen("detailClub")}}/>;
}

  /* =========================================================
  BLOC 16 - RENDU ECRAN DETAIL CLUB
========================================================= */
if (userConnected && screen === "detailClub" && clubSelected) {
 const contacts = contactsClub || [];
const actions = afficherToutesActionsClub
  ? actionsClub || []
  : (actionsClub || []).filter((action) => {
      if (filtrePeriode === "Toutes périodes") return true;
      return isActionDansPeriode(action);
    });
const historiques = historiquesClub || [];
const pieces = piecesClub || [];
const magasins = magasinsClub;
const totalMagDetail = totalMagasins(magasins);

async function supprimerActionCommerciale(action) {
  const ok = window.confirm("Voulez-vous vraiment supprimer cette action ?");
  if (!ok) return;
  const { error } = await supabaseClientActif.from("actions").delete().eq("id", action.id);
  if (error) {
    console.log("ERREUR DELETE ACTION :", error.code || error.message);
    alert("Erreur lors de la suppression de l'action");
    return;
  }
  setActionsClub((prev) => prev.filter((ligne) => ligne.id !== action.id));
  setToutesActions((prev) => prev.filter((ligne) => ligne.id !== action.id));
}
  return (
    <div style={stylePage} className="crm-club-page">
      <div
        style={{
          background: "white",
          padding: 20,
          borderRadius: 12,
          marginBottom: 20
        }}
      >
        <h2 id="section-infos">{clubSelected.code} <span>{clubSelected.nom}</span></h2>
        <p><strong>Code :</strong> {clubSelected.code}</p>
        <p><strong>Commercial :</strong> {clubSelected.commercial}</p>
        <button type="button" style={{ padding: 10, background: "#0866e9", color: "white", border: 0, borderRadius: 7 }} onClick={() => { setPropositionRdvInitiale({ club_id: clubSelected.id || clubSelected.identifiant, commercial: userConnected?.nom || clubSelected.commercial || "" }); setScreen("planning"); }}>
          Proposer un rendez-vous
        </button>

{userConnected?.nom === "Bruno" && (
  <select
    style={{ padding: 10, margin: 10, width: 250 }}
    value={clubSelected.commercial || ""}
    onChange={(e) =>
      setClubSelected({ ...clubSelected, commercial: e.target.value })
    }
  >
    <option>Bruno</option>
    <option>Adrien</option>
    <option>Hervé</option>
    <option>Non attribué</option>
  </select>
)}

        <div style={{ marginTop: 10 }}>
          <input
            placeholder="Nom du club"
            style={{ padding: 10, margin: 10, width: 250 }}
            value={clubSelected.nom || ""}
            onChange={(e) =>
              setClubSelected({ ...clubSelected, nom: e.target.value })
            }
          />
        </div>

        <div>
          <input
            placeholder="Ville"
            style={{ padding: 10, margin: 10, width: 250 }}
            value={clubSelected.ville || ""}
            onChange={(e) =>
              setClubSelected({ ...clubSelected, ville: e.target.value })
            }
          />
        </div>

        <div>
          <select
            style={{ padding: 10, margin: 10, width: 272 }}
            value={clubSelected.sport || ""}
            onChange={(e) =>
              setClubSelected({ ...clubSelected, sport: e.target.value })
            }
          >
            <option value="">Choisir un sport</option>
            {sportsOptions.map((sport) => (
              <option key={sport} value={sport}>
                {sport}
              </option>
            ))}
          </select>
        </div>

        <div style={{ margin: 10 }}>
          <input
            placeholder="Ajouter un nouveau sport"
            style={{ padding: 10, width: 250, marginRight: 10 }}
            value={nouveauSportClub}
            onChange={(e) => setNouveauSportClub(e.target.value)}
          />
          <button
            style={{ padding: 10 }}
            onClick={() => {
              const valeur = ajouterSportOption(nouveauSportClub);
              if (valeur !== "") {
                setClubSelected({ ...clubSelected, sport: valeur });
                setNouveauSportClub("");
              }
            }}
          >
            Ajouter ce sport
          </button>
        </div>

        <div>
          <input
            placeholder="Nombre d’adhérents"
            style={{ padding: 10, margin: 10, width: 250 }}
            value={clubSelected.adherents || ""}
            onChange={(e) =>
              setClubSelected({ ...clubSelected, adherents: e.target.value })
            }
          />
        </div>

        <div>
          <input
            placeholder="Potentiel"
            style={{ padding: 10, margin: 10, width: 250 }}
            value={clubSelected.potentiel || ""}
            onChange={(e) =>
              setClubSelected({ ...clubSelected, potentiel: e.target.value })
            }
          />
        </div>

        <div>
          <input
            placeholder="N° compte client Interclub"
            style={{ padding: 10, margin: 10, width: 250 }}
            value={clubSelected.numero_compte_interclub || ""}
            onChange={(e) =>
              setClubSelected({
                ...clubSelected,
                numero_compte_interclub: e.target.value
              })
            }
          />
        </div>
      </div>

      <div
        style={{
          background: "white",
          padding: 20,
          borderRadius: 12,
          marginBottom: 20
        }}
      >
        <h3 id="section-magasins">Rattachement magasin</h3>

        {magasins.map((mag, index) => (
          <div
            key={index}
            style={{
              background: "#fafafa",
              padding: 10,
              margin: 10,
              border: "1px solid #ddd",
              borderRadius: 8
            }}
          >
            <input
              placeholder="Nom du magasin"
              style={{ padding: 10, marginRight: 10, width: 180 }}
              value={mag.nom}
              onChange={(e) =>
                updateMagasinDetail(index, "nom", e.target.value)
              }
            />

            <input
              type="number"
              placeholder="%"
              style={{ padding: 10, marginRight: 10, width: 90 }}
              value={mag.taux}
              onChange={(e) =>
                updateMagasinDetail(index, "taux", e.target.value)
              }
            />

            <input placeholder="Adresse" style={{ padding: 10, marginRight: 10, width: 220 }} value={mag.adresse || ""} onChange={(e) => updateMagasinDetail(index, "adresse", e.target.value)}/>
            <input placeholder="Code postal" style={{ padding: 10, marginRight: 10, width: 110 }} value={mag.code_postal || ""} onChange={(e) => updateMagasinDetail(index, "code_postal", e.target.value)}/>
            <input placeholder="Ville" style={{ padding: 10, marginRight: 10, width: 160 }} value={mag.ville || ""} onChange={(e) => updateMagasinDetail(index, "ville", e.target.value)}/>
            <input placeholder="Téléphone" style={{ padding: 10, marginRight: 10, width: 150 }} value={mag.telephone || ""} onChange={(e) => updateMagasinDetail(index, "telephone", e.target.value)}/>
            <input type="email" placeholder="E-mail" style={{ padding: 10, marginRight: 10, width: 220 }} value={mag.email || ""} onChange={(e) => updateMagasinDetail(index, "email", e.target.value)}/>
            <label style={{ marginRight: 10 }}><input type="checkbox" checked={mag.actif !== false} onChange={(e) => updateMagasinDetail(index, "actif", e.target.checked)}/> Actif</label>

            <button
              style={{
                padding: 10,
                background: "#b71c1c",
                color: "white",
                border: "none",
                borderRadius: 6
              }}
              onClick={() => removeMagasinDetail(index)}
            >
              Supprimer
            </button>
          </div>
        ))}

        <button
          style={{ padding: 10, margin: 10 }}
          onClick={addMagasinDetail}
        >
          + Ajouter un magasin
        </button>

        <div style={{ margin: 10, padding: 10, background: "#fff" }}>
          <strong>Total magasins : {totalMagDetail}%</strong>
          {totalMagDetail !== 100 && (
            <div style={{ color: "#b71c1c", marginTop: 5 }}>
              Attention : le total doit idéalement être de 100%.
            </div>
          )}
        </div>
      </div>

      <div
        style={{
          background: "white",
          padding: 20,
          borderRadius: 12,
          marginBottom: 20
        }}
      >
       <h3 id="section-contacts">Contacts</h3>

        {contacts.map((contact, index) => {
          const telephoneNettoye = String(contact.telephone || "").replace(/\D/g, "");
          const telephoneWhatsApp =
            telephoneNettoye.startsWith("0") && telephoneNettoye.length === 10
              ? `33${telephoneNettoye.slice(1)}`
              : telephoneNettoye;

          return (
            <div
              key={contact.id || index}
              style={{
                border: "1px solid #ccc",
                padding: 10,
                margin: 10,
                background: "#f9f9f9",
                borderRadius: 8
              }}
            >
              {contactEditionId===contact.id ? <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:8}}>
                <input aria-label="Modifier le nom du contact" value={contactEdition?.nom||''} onChange={(e)=>setContactEdition((v)=>({...v,nom:e.target.value}))}/>
                <input aria-label="Modifier la fonction du contact" value={contactEdition?.fonction||''} onChange={(e)=>setContactEdition((v)=>({...v,fonction:e.target.value}))}/>
                <input aria-label="Modifier le téléphone du contact" value={contactEdition?.telephone||''} onChange={(e)=>setContactEdition((v)=>({...v,telephone:e.target.value}))}/>
                <input aria-label="Modifier le mail du contact" type="email" value={contactEdition?.mail||''} onChange={(e)=>setContactEdition((v)=>({...v,mail:e.target.value}))}/>
              </div> : <><div><strong>{contact.nom}</strong></div><div>{contact.fonction}</div><div style={{marginTop:4}}>{contact.telephone||''}</div><div style={{marginTop:4}}>{contact.mail||''}</div></>}

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
                {contact.telephone && (
                  <a
                    href={`tel:${contact.telephone}`}
                    style={{ textDecoration: "none" }}
                  >
                    <button
                      type="button"
                      style={{
                        padding: 8,
                        background: "#2e7d32",
                        color: "white",
                        border: "none",
                        borderRadius: 6,
                        cursor: "pointer"
                      }}
                    >
                      Appeler
                    </button>
                  </a>
                )}

                {telephoneWhatsApp && (
                  <a
                    href={`https://wa.me/${telephoneWhatsApp}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ textDecoration: "none" }}
                  >
                    <button
                      type="button"
                      style={{
                        padding: 8,
                        background: "#25D366",
                        color: "white",
                        border: "none",
                        borderRadius: 6,
                        cursor: "pointer"
                      }}
                    >
                      WhatsApp
                    </button>
                  </a>
                )}

                {contact.mail && (
                  <a
                    href={`mailto:${contact.mail}?subject=Contact%20Intersport&body=Bonjour%20${encodeURIComponent(contact.nom || "")},`}
                    style={{ textDecoration: "none" }}
                  >
                    <button
                      type="button"
                      style={{
                        padding: 8,
                        background: "#1565c0",
                        color: "white",
                        border: "none",
                        borderRadius: 6,
                        cursor: "pointer"
                      }}
                    >
                      Mail
                    </button>
                  </a>
                )}

                <button type="button" style={{ padding: 8, background: "#0866e9", color: "white", border: "none", borderRadius: 6 }} onClick={() => { setPropositionRdvInitiale({ club_id: clubSelected.id || clubSelected.identifiant, contact_id: contact.id, commercial: userConnected?.nom || clubSelected.commercial || "" }); setScreen("planning"); }}>
                  Proposer un rendez-vous
                </button>

                {contactEditionId===contact.id ? <><button type="button" style={{padding:8}} onClick={enregistrerModificationContact}>Enregistrer les modifications</button><button type="button" style={{padding:8}} onClick={()=>{setContactEditionId(null);setContactEdition(null);}}>Annuler</button></> : <button type="button" style={{padding:8}} onClick={()=>{setContactEditionId(contact.id);setContactEdition({...contact});}}>Modifier ce contact</button>}

                <button
                  style={{
                    padding: 8,
                    background: "#b71c1c",
                    color: "white",
                    border: "none",
                    borderRadius: 6
                  }}
                  onClick={() => supprimerContact(contact.id)}
                >
                  Supprimer ce contact
                </button>
              </div>
            </div>
          );
        })}

        <h4>Ajouter un contact</h4>

        {alerteDoublonContact !== "" && (
          <div
            style={{
              background: "#fff3cd",
              padding: 10,
              margin: 10,
              border: "1px solid #ffec99"
            }}
          >
            {alerteDoublonContact}
          </div>
        )}

        <div>
          <input
            placeholder="Nom"
            style={{ padding: 10, margin: 10, width: 250 }}
            value={contactNom}
            onChange={(e) => setContactNom(e.target.value)}
          />
        </div>

        <div>
          <input
            placeholder="Fonction"
            style={{ padding: 10, margin: 10, width: 250 }}
            value={contactFonction}
            onChange={(e) => setContactFonction(e.target.value)}
          />
        </div>

        <div>
          <input
            placeholder="Téléphone"
            style={{ padding: 10, margin: 10, width: 250 }}
            value={contactTelephone}
            onChange={(e) => {
              const value = e.target.value;
              setContactTelephone(value);
              setAlerteDoublonContact(
                detecterDoublonContact(value, contactMail)
              );
            }}
          />
        </div>

        <div>
          <input
            placeholder="Mail"
            style={{ padding: 10, margin: 10, width: 250 }}
            value={contactMail}
            onChange={(e) => {
              const value = e.target.value;
              setContactMail(value);
              setAlerteDoublonContact(
                detecterDoublonContact(contactTelephone, value)
              );
            }}
          />
        </div>

        <button
          style={{ padding: 10, marginTop: 10, marginRight: 10 }}
          onClick={async () => {
            const clubId = clubSelected.id || clubSelected.identifiant;

            if (!clubId) {
              alert("Identifiant du club introuvable");
              return;
            }

            const nouveauContact = {
              club_id: clubId,
              nom: contactNom,
              fonction: contactFonction,
              telephone: contactTelephone,
              mail: contactMail
            };

            const { data, error } = await supabase
              .from("contacts")
              .insert([nouveauContact])
              .select()
              .single();

            if (error) {
              console.log("ERREUR INSERT CONTACT :", error);
              alert("Erreur lors de l'enregistrement du contact");
              return;
            }

            setContactsClub((prev) => [...prev, data]);
            setAllContacts((prev) => [...prev, data]);

            setContactNom("");
            setContactFonction("");
            setContactTelephone("");
            setContactMail("");
            setAlerteDoublonContact("");
          }}
        >
          Ajouter contact
        </button>
      </div>

      <div
        style={{
          background: "white",
          padding: 20,
          borderRadius: 12,
          marginBottom: 20
        }}
      >
<h3 id="section-actions">Actions commerciales</h3>
<p className="crm-club-filter-summary"><strong>Synthèse du filtre :</strong> {afficherToutesActionsClub ? 'Toutes périodes' : filtrePeriode} · {rechercheActionDocument.trim() ? 'Recherche documentaire active — résultats ci-dessous' : `${actions.length} action${actions.length > 1 ? 's' : ''} sur la période`}</p>
<div style={{ marginBottom: 10 }}>
  <button style={{ padding: 9, marginRight: 8, background: "white", color: "#1565c0", border: "1px solid #1565c0", borderRadius: 6 }} onClick={() => { setDevisInitialId(null); setDevisInterclubInitialId(null); setDevisDepuisGeneral(false); setScreen("devisCRM"); }}>VOIR LES DEVIS</button>
  <button style={{ padding: 9, background: "#1565c0", color: "white", border: 0, borderRadius: 6, fontWeight: "bold" }} onClick={() => { setDevisInitialId(null); setDevisInterclubInitialId(null); setDevisDepuisGeneral(false); setScreen("devisCRMNouveau"); }}>+ NOUVEAU DEVIS</button>
  {BOUTIQUECLUB_ENABLED && ["superviseur", "gestionnaire", "commercial"].includes(userConnected?.role) && <button style={{ marginLeft: 8, padding: 9, background: "#6a1b9a", color: "white", border: 0, borderRadius: 6, fontWeight: "bold" }} onClick={() => { setBoutiqueClubClubInitial(clubSelected); setBoutiqueClubDestinationInitiale(null); setScreen("boutiqueClub"); }}>+ CRÉER UNE CAMPAGNE BOUTIQUECLUB</button>}
</div>
<div id="section-contrats" style={{ marginBottom: 12, padding: 10, background: "#eef8f5", borderRadius: 8 }}><strong style={{ marginRight: 12 }}>CONTRATS</strong><button style={{ padding: 9, marginRight: 8 }} onClick={() => { setContratInitialId(null); setContratRenouvelerId(null); setScreen("contratsCRM"); }}>VOIR LES CONTRATS</button><button style={{ padding: 9, background: "#0d5f4d", color: "white", border: 0, borderRadius: 6 }} onClick={() => { setContratInitialId("__NEW__"); setContratRenouvelerId(null); setScreen("contratsCRM"); }}>+ NOUVEAU CONTRAT</button></div>
<button
  style={{ padding: 8, marginBottom: 10 }}
  onClick={() => setAfficherToutesActionsClub(!afficherToutesActionsClub)}
>
  {afficherToutesActionsClub ? "Revenir au filtre dashboard" : "Afficher toutes les actions"}
</button>
<input
  placeholder="Filtrer par n° devis / commande / facture"
  value={rechercheActionDocument}
  onChange={(e) => setRechercheActionDocument(e.target.value)}
  style={{
    padding: 10,
    margin: 10,
    width: 320,
    maxWidth: "90%"
  }}
/>
<CycleCommercialInterclub
  clientSupabase={supabaseClientActif}
  club={clubSelected}
  documentsInterclub={documentsInterclub}
  actions={actions}
  recherche={rechercheActionDocument}
  erreurInterclub={erreurDocumentsInterclub}
  onSupprimerAction={supprimerActionCommerciale}
/>

        <h4>Ajouter une action</h4>

        <div>
          <select
            style={{ padding: 10, margin: 10, width: 272 }}
            value={actionType}
            onChange={(e) => setActionType(e.target.value)}
          >
            <option>Visite club</option>
            <option>Prospection</option>
            <option>Offre</option>
            <option>Relance</option>
            <option>Commande</option>
            <option>Livraison</option>
            <option>Facturé</option>
            <option>Payé</option>
            <option>Animation club</option>
            <option>Soirée privée</option>
            <option>Suivi</option>
          </select>
        </div>

        <div>
          <input
            type="date"
            style={{ padding: 10, margin: 10, width: 250 }}
            value={actionDate}
            onChange={(e) => setActionDate(e.target.value)}
          />
        </div>

        <div>
          <input
            type="number"
            min="0"
            step="0.01"
            placeholder="Montant TTC (€) — facultatif"
            style={{ padding: 10, margin: 10, width: 250 }}
            value={actionMontantTTC}
            onChange={(e) => setActionMontantTTC(e.target.value)}
          />
        </div>

        <div>
          <textarea
            placeholder="Commentaire"
            value={actionCommentaire}
            onChange={(e) => setActionCommentaire(e.target.value)}
            rows={5}
            style={{ padding: 10, margin: 10, width: 250, resize: "vertical" }}
          />
        </div>

        <button
          style={{ padding: 10, marginTop: 10, marginRight: 10 }}
          onClick={async () => {
            const clubId = clubSelected.id || clubSelected.identifiant;

            if (!clubId) {
              alert("Identifiant du club introuvable");
              return;
            }

            const nouvelleAction = {
              club_id: clubId,
              type: actionType,
              date: actionDate,
              commentaire: actionCommentaire,
              montantTTC: actionMontantTTC === "" ? null : Number(actionMontantTTC)
            };

            const { data, error } = await supabase
              .from("actions")
              .insert([nouvelleAction])
              .select()
              .single();

            if (error) {
              console.log("ERREUR INSERT ACTION :", error);
              alert("Erreur lors de l'enregistrement de l'action");
              return;
            }

            setActionsClub((prev) => [data, ...prev]);
            setToutesActions((prev) => [data, ...prev]);

            setActionType("Visite club");
            setActionDate(dateDuJourInput);
            setActionCommentaire("");
            setActionMontantTTC("");
          }}
        >
          Ajouter action
        </button>
      </div>

      <div
        style={{
          background: "white",
          padding: 20,
          borderRadius: 12,
          marginBottom: 20
        }}
      >
        <h3 id="section-historique">Historique</h3>

        {historiques.map((ligne, index) => (
          <div
            key={ligne.id || index}
            style={{
              border: "1px solid #ddd",
              padding: 10,
              margin: 10,
              background: "#f6f2ff",
              borderRadius: 8
            }}
          >
            <div><strong>{ligne.type}</strong></div>
            <div>{ligne.date}</div>
            <div>{ligne.commentaire}</div>

            <button
              style={{
                padding: 8,
                marginTop: 10,
                background: "#b71c1c",
                color: "white",
                border: "none",
                borderRadius: 6
              }}
              onClick={async () => {
                const ok = window.confirm(
                  "Voulez-vous vraiment supprimer cet élément d’historique ?"
                );
                if (!ok) return;

                const { error } = await supabase
                  .from("historiques")
                  .delete()
                  .eq("id", ligne.id);

                if (error) {
                  console.log("ERREUR DELETE HISTORIQUE :", error);
                  alert("Erreur lors de la suppression de l'historique");
                  return;
                }

                setHistoriquesClub((prev) => prev.filter((h) => h.id !== ligne.id));
              }}
            >
              Supprimer
            </button>
          </div>
        ))}

        <h4>Ajouter un élément d’historique</h4>

        <div>
          <select
            style={{ padding: 10, margin: 10, width: 272 }}
            value={historiqueType}
            onChange={(e) => setHistoriqueType(e.target.value)}
          >
            {historiqueTypeOptions.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        <div style={{ margin: 10 }}>
          <input
            placeholder="Ajouter un nouveau type"
            style={{ padding: 10, width: 250, marginRight: 10 }}
            value={historiqueNouveauType}
            onChange={(e) => setHistoriqueNouveauType(e.target.value)}
          />
          <button
            style={{ padding: 10 }}
            onClick={() => {
              const valeur = ajouterHistoriqueTypeOption(historiqueNouveauType);
              if (valeur !== "") {
                setHistoriqueType(valeur);
                setHistoriqueNouveauType("");
              }
            }}
          >
            Ajouter ce type
          </button>
        </div>

        <div>
          <input
            type="month"
            style={{ padding: 10, margin: 10, width: 250 }}
            value={historiqueDate}
            onChange={(e) => setHistoriqueDate(e.target.value)}
          />
        </div>

        <div>
          <textarea
            placeholder="Commentaire"
            style={{ padding: 10, margin: 10, width: 250, minHeight: 90 }}
            value={historiqueCommentaire}
            onChange={(e) => setHistoriqueCommentaire(e.target.value)}
          />
        </div>

        <button
          style={{ padding: 10, marginTop: 10, marginRight: 10 }}
          onClick={async () => {
            const clubId = clubSelected.id || clubSelected.identifiant;

            if (!clubId) {
              alert("Identifiant du club introuvable");
              return;
            }

            const nouvelleLigne = {
              club_id: clubId,
              type: historiqueType,
              date: historiqueDate,
              commentaire: historiqueCommentaire
            };

            const { data, error } = await supabase
              .from("historiques")
              .insert([nouvelleLigne])
              .select()
              .single();

            if (error) {
              console.log("ERREUR INSERT HISTORIQUE :", error);
              alert("Erreur lors de l'enregistrement de l'historique");
              return;
            }

            setHistoriquesClub((prev) => [data, ...prev]);

            setHistoriqueType(historiqueTypeOptions[0] || "Concurrence");
            setHistoriqueDate(moisDuJourInput);
            setHistoriqueCommentaire("");
          }}
        >
          Ajouter historique
        </button>
      </div>

      <div
        style={{
          background: "white",
          padding: 20,
          borderRadius: 12,
          marginBottom: 20
        }}
      >
        <h3 id="section-pj">Pièces jointes</h3>

        {pieces.map((p, index) => (
          <div
            key={p.id || index}
            style={{
              border: "1px solid #ccc",
              padding: 10,
              margin: 10,
              background: "#fff",
              borderRadius: 8
            }}
          >
            <div><strong>{p.nom}</strong></div>

            {p.type && p.type.startsWith("image") && (
              <div style={{ marginTop: 8 }}>
                <img
                  src={p.url}
                  alt={p.nom}
                  style={{ width: 120, marginTop: 5, cursor: "pointer" }}
                  onClick={() => window.open(p.url, "_blank")}
                />

                <div style={{ marginTop: 10 }}>
                  <button
                    style={{ padding: 8, marginRight: 10 }}
                    onClick={() => window.open(p.url, "_blank")}
                  >
                    Ouvrir en grand
                  </button>

                  <button
                    style={{ padding: 8, marginRight: 10 }}
                    onClick={() => {
                      const fenetre = window.open(p.url, "_blank");
                      if (fenetre) {
                        fenetre.onload = () => fenetre.print();
                      }
                    }}
                  >
                    Imprimer
                  </button>
                </div>
              </div>
            )}

            {p.type === "application/pdf" && (
              <div style={{ marginTop: 8 }}>
                <a href={p.url} target="_blank" rel="noreferrer">
                  Ouvrir le PDF
                </a>

                <div style={{ marginTop: 10 }}>
                  <button
                    style={{ padding: 8, marginRight: 10 }}
                    onClick={() => window.open(p.url, "_blank")}
                  >
                    Ouvrir
                  </button>

                  <button
                    style={{ padding: 8, marginRight: 10 }}
                    onClick={() => {
                      const fenetre = window.open(p.url, "_blank");
                      if (fenetre) {
                        fenetre.onload = () => fenetre.print();
                      }
                    }}
                  >
                    Imprimer
                  </button>
                </div>
              </div>
            )}

            <button
              style={{
                padding: 8,
                marginTop: 10,
                background: "#b71c1c",
                color: "white",
                border: "none",
                borderRadius: 6
              }}
              onClick={async () => {
                const ok = window.confirm(
                  "Voulez-vous vraiment supprimer cette pièce jointe ?"
                );
                if (!ok) return;

                if (p.chemin) {
                  const { error: storageError } = await supabaseClientActif.storage
                    .from("pieces-clubs")
                    .remove([p.chemin]);

                  if (storageError) {
                    console.log("ERREUR DELETE STORAGE :", storageError);
                  }
                }

                const { error } = await supabaseClientActif
                  .from("pieces_jointes")
                  .delete()
                  .eq("id", p.id);

                if (error) {
                  console.log("ERREUR DELETE PIECE :", error);
                  alert("Erreur lors de la suppression de la pièce jointe");
                  return;
                }

                setPiecesClub((prev) => prev.filter((piece) => piece.id !== p.id));
              }}
            >
              Supprimer cette pièce jointe
            </button>
          </div>
        ))}

        <div style={{ margin: 10 }}>
          <div style={{ marginBottom: 10 }}>
            <strong>Choisir un fichier</strong>
          </div>
          <input
            type="file"
            accept="image/*,.pdf"
            onChange={(e) => {
              const file = e.target.files[0];
              chargerPiece(file);
            }}
          />
        </div>

        <div style={{ margin: 10 }}>
          <div style={{ marginBottom: 10 }}>
            <strong>Prendre une photo</strong>
          </div>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => {
              const file = e.target.files[0];
              chargerPiece(file);
            }}
          />
        </div>

        {pieceNom !== "" && (
          <div
            style={{
              margin: 10,
              padding: 10,
              background: "#f5f5f5",
              borderRadius: 8
            }}
          >
            <div>
              Fichier sélectionné : <strong>{pieceNom}</strong>
            </div>

            <button
              style={{ padding: 10, marginTop: 10, marginRight: 10 }}
              onClick={async () => {
                const clubId = clubSelected.id || clubSelected.identifiant;

                if (!clubId) {
                  alert("Identifiant du club introuvable");
                  return;
                }

                if (!pieceData || !pieceNom) {
                  alert("Aucun fichier sélectionné");
                  return;
                }

                const response = await fetch(pieceData);
                const blob = await response.blob();

                const nomNettoye = pieceNom.replace(/[^a-zA-Z0-9._-]/g, "_");
                const chemin = `${groupeSelectionne}/${clubId}/${Date.now()}-${nomNettoye}`;

                const { error: uploadError } = await supabaseClientActif.storage
                  .from("pieces-clubs")
                  .upload(chemin, blob, {
                    contentType: pieceType,
                    upsert: false
                  });

                if (uploadError) {
                  console.log("ERREUR UPLOAD PIECE :", uploadError);
                  alert("Erreur lors de l'envoi du fichier");
                  return;
                }

                const nouvellePiece = {
                  club_id: clubId,
                  nom: pieceNom,
                  type: pieceType,
                  chemin: chemin,
                  url: null
                };

                const { data, error } = await supabaseClientActif
                  .from("pieces_jointes")
                  .insert([nouvellePiece])
                  .select()
                  .single();

                if (error) {
                  console.log("ERREUR INSERT PIECE :", error);
                  await supabaseClientActif.storage.from("pieces-clubs").remove([chemin]);
                  alert("Erreur lors de l'enregistrement de la pièce jointe");
                  return;
                }

                const { data: acces } = await supabaseClientActif.storage
                  .from("pieces-clubs")
                  .createSignedUrl(chemin, 3600);
                setPiecesClub((prev) => [{ ...data, url: acces?.signedUrl || "" }, ...prev]);

                setPieceNom("");
                setPieceType("");
                setPieceData("");
              }}
            >
              Ajouter pièce jointe
            </button>

            <button
              style={{ padding: 10, marginTop: 10 }}
              onClick={() => {
                setPieceNom("");
                setPieceType("");
                setPieceData("");
              }}
            >
              Annuler
            </button>
          </div>
        )}
      </div>

<div
  style={{
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    backgroundColor: "white",
    padding: 10,
    borderTop: "2px solid #ddd",
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    alignItems: "center",
  }}
>
        <button
          style={{ padding: 10, marginRight: 10 }}
          onClick={async () => {
            const clubId = clubSelected.id || clubSelected.identifiant;

            const { error } = await supabase
              .from("clubs")
              .update({
                nom: clubSelected.nom,
                ville: clubSelected.ville,
                sport: clubSelected.sport,
                adherents: clubSelected.adherents,
                potentiel: clubSelected.potentiel,
                numero_compte_interclub: clubSelected.numero_compte_interclub || null,
commercial: clubSelected.commercial || null
              })
              .eq("id", clubId);

            if (error) {
              console.log("ERREUR UPDATE CLUB :", error);
              alert("Erreur lors de la mise à jour du club");
              return;
            }

            try {
              await sauvegarderMagasinsClub(supabase, clubId, magasinsClub, magasinsClubSupprimes);
              setMagasinsClubSupprimes([]);
            } catch (erreurMagasins) {
              console.log("ERREUR MISE À JOUR MAGASINS CLUB :", erreurMagasins);
              alert("Erreur lors de l'enregistrement des magasins");
              return;
            }

            await chargerClubs();
            await chargerMagasinsClub(clubId);

            setScreen(screenRetourClub);
            setScreenRetourClub("dashboard");

            setPieceNom("");
            setPieceType("");
            setPieceData("");
          }}
        >
          Enregistrer modifications
        </button>

        <button
          style={{
            padding: 10,
            marginRight: 10,
            background: "#b71c1c",
            color: "white",
            border: "none",
            borderRadius: 6
          }}
          onClick={async () => {
            const ok = window.confirm(
              "Voulez-vous vraiment supprimer ce club ?"
            );
            if (!ok) return;

            const clubId = clubSelected.id || clubSelected.identifiant;

            const { error } = await supabase.rpc("crm_club_supprimer", {
              p_club_id: clubId
            });

            if (error) {
              console.log("ERREUR DELETE CLUB :", error);
              alert(error.message || "La suppression du club a été refusée.");
              return;
            }

            await chargerClubs();
            await chargerTousContacts();
            await chargerToutesActions();

            setClubSelected(null);
            setScreen(screenRetourClub);
            setScreenRetourClub("dashboard");

            setPieceNom("");
            setPieceType("");
            setPieceData("");
          }}
        >
          Supprimer ce club
        </button>

        <button
          style={{ padding: 10 }}
          onClick={() => {
            setScreen(screenRetourClub);
            setScreenRetourClub("dashboard");
            setPieceNom("");
            setPieceType("");
            setPieceData("");
            setAlerteDoublonContact("");
          }}
        >
          Retour
        </button>
<button
  onClick={() =>
    document
      .getElementById("section-infos")
      ?.scrollIntoView({ behavior: "smooth" })
  }
>
  Infos
</button>

<button
  onClick={() =>
    document
      .getElementById("section-magasins")
      ?.scrollIntoView({ behavior: "smooth" })
  }
>
  Magasins
</button>

<button
  onClick={() =>
    document
      .getElementById("section-contacts")
      ?.scrollIntoView({ behavior: "smooth" })
  }
>
  Contacts
</button>

<button
  onClick={() =>
    document
      .getElementById("section-actions")
      ?.scrollIntoView({ behavior: "smooth" })
  }
>
  Actions
</button>

<button onClick={() => { setDevisInitialId(null); setDevisInterclubInitialId(null); setDevisDepuisGeneral(false); setScreen("devisCRM"); }}>
  DEVIS
</button>

{BOUTIQUECLUB_ENABLED && ["superviseur", "gestionnaire", "commercial"].includes(userConnected?.role) && <button onClick={() => { setBoutiqueClubClubInitial(clubSelected); setBoutiqueClubDestinationInitiale(null); setScreen("boutiqueClub"); }}>
  BOUTIQUECLUB
</button>}

<button onClick={() => { setContratInitialId(null); setContratRenouvelerId(null); setScreen("contratsCRM"); }}>
  CONTRATS
</button>

<button
  onClick={() =>
    document
      .getElementById("section-historique")
      ?.scrollIntoView({ behavior: "smooth" })
  }
>
  Historique
</button>

<button
  onClick={() =>
    document
      .getElementById("section-pj")
      ?.scrollIntoView({ behavior: "smooth" })
  }
>
  PJ
</button>		
      </div>
    </div>
  );
}
 /* =========================================================
  BLOC 17 - RENDU ECRAN CREATION CLUB
========================================================= */
if (userConnected && screen === "club") {
  const totalMagCreation = totalMagasins(magasinsCreation);

  return (
    <div style={stylePage}>
      <div
        style={{
          background: "white",
          padding: 20,
          borderRadius: 12
        }}
      >
        <h2>Créer un club</h2>

        <p>Code club : attribué automatiquement à l’enregistrement</p>
        <p>Commercial : {userConnected?.nom || ""}</p>

        {alerteDoublonClub !== "" && (
          <div
            style={{
              background: "#fff3cd",
              padding: 10,
              margin: 10,
              border: "1px solid #ffec99"
            }}
          >
            {alerteDoublonClub}
          </div>
        )}

        <input
          placeholder="Nom du club"
          style={{ padding: 10, margin: 10 }}
          value={nomClub}
          onChange={(e) => {
            const value = e.target.value;
            setNomClub(value);
            setAlerteDoublonClub(detecterDoublonClub(value));
          }}
        />

        <input
          placeholder="Ville"
          style={{ padding: 10, margin: 10 }}
          value={villeClub}
          onChange={(e) => setVilleClub(e.target.value)}
        />

        <div>
          <select
            style={{ padding: 10, margin: 10, width: 272 }}
            value={sportClub}
            onChange={(e) => setSportClub(e.target.value)}
          >
            <option value="">Choisir un sport</option>
            {sportsOptions.map((sport) => (
              <option key={sport} value={sport}>
                {sport}
              </option>
            ))}
          </select>
        </div>

        <div style={{ margin: 10 }}>
          <input
            placeholder="Ajouter un nouveau sport"
            style={{ padding: 10, width: 250, marginRight: 10 }}
            value={nouveauSportClub}
            onChange={(e) => setNouveauSportClub(e.target.value)}
          />
          <button
            style={{ padding: 10 }}
            onClick={() => {
              const valeur = ajouterSportOption(nouveauSportClub);
              if (valeur !== "") {
                setSportClub(valeur);
                setNouveauSportClub("");
              }
            }}
          >
            Ajouter ce sport
          </button>
        </div>

        <input
          placeholder="Nombre d’adhérents"
          style={{ padding: 10, margin: 10 }}
          value={adherentsClub}
          onChange={(e) => setAdherentsClub(e.target.value)}
        />

        <input
          placeholder="Potentiel TTC"
          style={{ padding: 10, margin: 10 }}
          value={potentielClub}
          onChange={(e) => setPotentielClub(e.target.value)}
        />

        <input
          placeholder="N° compte client Interclub"
          style={{ padding: 10, margin: 10 }}
          value={numeroCompteInterclub}
          onChange={(e) => setNumeroCompteInterclub(e.target.value)}
        />

        <h3 style={{ marginTop: 20 }}>Rattachement magasins</h3>

        {magasinsCreation.map((mag, index) => (
          <div
            key={index}
            style={{
              background: "#fafafa",
              padding: 10,
              margin: 10,
              border: "1px solid #ddd",
              borderRadius: 8
            }}
          >
            <input
              placeholder="Nom du magasin"
              style={{ padding: 10, marginRight: 10, width: 180 }}
              value={mag.nom}
              onChange={(e) =>
                updateMagasinCreation(index, "nom", e.target.value)
              }
            />

            <input
              type="number"
              placeholder="%"
              style={{ padding: 10, marginRight: 10, width: 90 }}
              value={mag.taux}
              onChange={(e) =>
                updateMagasinCreation(index, "taux", e.target.value)
              }
            />

            <button
              style={{
                padding: 10,
                background: "#b71c1c",
                color: "white",
                border: "none",
                borderRadius: 6
              }}
              onClick={() => removeMagasinCreation(index)}
            >
              Supprimer
            </button>
          </div>
        ))}

        <button
          style={{ padding: 10, margin: 10 }}
          onClick={addMagasinCreation}
        >
          + Ajouter un magasin
        </button>

        <div style={{ margin: 10, padding: 10, background: "#fff" }}>
          <strong>Total magasins : {totalMagCreation}%</strong>
          {totalMagCreation !== 100 && (
            <div style={{ color: "#b71c1c", marginTop: 5 }}>
              Attention : le total doit idéalement être de 100%.
            </div>
          )}
        </div>

        <br />

        <button
          style={{ padding: 10 }}
          onClick={async () => {
            const nouveauClub = {
              nom: nomClub,
              ville: villeClub,
              sport: sportClub || "",
              adherents: adherentsClub ? Number(adherentsClub) : 0,
              potentiel: potentielClub ? Number(potentielClub) : 0,
              numero_compte_interclub: numeroCompteInterclub.trim() || null,
              commercial: userConnected?.nom || ""
            };

            const { data: clubCree, error } = await supabase
              .from("clubs")
              .insert([nouveauClub])
              .select()
              .single();

            if (error) {
              console.log("ERROR INSERT CLUB :", error);
              alert("Erreur lors de l'enregistrement du club : " + error.message);
              return;
            }

            const clubId = clubCree?.id || clubCree?.identifiant;

            if (!clubCree || !clubId) {
              console.log("CLUB CREE :", clubCree);
              alert("Club créé mais identifiant introuvable");
              return;
            }

            const magasinsAInserer = (magasinsCreation || []).map((mag) => ({
              club_id: clubId,
              nom: mag.nom,
              taux: Number(mag.taux) || 0
            }));

            if (magasinsAInserer.length > 0) {
              const { error: errorMagasins } = await supabase
                .from("magasins_club")
                .insert(magasinsAInserer);

              if (errorMagasins) {
                console.log("ERROR INSERT MAGASINS :", errorMagasins);
                alert("Club créé, mais erreur lors de l'enregistrement des magasins");
                return;
              }
            }

            await chargerClubs();

            setNomClub("");
            setVilleClub("");
            setSportClub("");
            setNouveauSportClub("");
            setAdherentsClub("");
            setPotentielClub("");
            setNumeroCompteInterclub("");
            setMagasinsCreation([{ nom: "L’Arbresle", taux: "100" }]);
            setAlerteDoublonClub("");

            setScreen("dashboard");
          }}
        >
          Enregistrer
        </button>

        <button
          style={{ padding: 10, marginLeft: 10 }}
          onClick={() => {
            setScreen("dashboard");
            setAlerteDoublonClub("");
          }}
        >
          Retour
        </button>
      </div>
    </div>
  );
}

/* =========================================================
  BLOC 17B - RENDU ECRAN OBJECTIFS
========================================================= */
if (userConnected?.role === "superviseur" && screen === "objectifs") {
  const moisExercice = [
    { label: "Avril", mois: 4 },
    { label: "Mai", mois: 5 },
    { label: "Juin", mois: 6 },
    { label: "Juillet", mois: 7 },
    { label: "Août", mois: 8 },
    { label: "Septembre", mois: 9 },
    { label: "Octobre", mois: 10 },
    { label: "Novembre", mois: 11 },
    { label: "Décembre", mois: 12 },
    { label: "Janvier", mois: 1 },
    { label: "Février", mois: 2 },
    { label: "Mars", mois: 3 }
  ];

  function getAnneeDuMoisExercice(mois, exerciceDebut) {
    return mois >= 4 ? exerciceDebut : exerciceDebut + 1;
  }

  function getObjectifLigne(commercial, exerciceDebut, mois, indicateur) {
    const annee = getAnneeDuMoisExercice(mois, exerciceDebut);

    return objectifs.find(
      (obj) =>
        String(obj.commercial || "").toLowerCase() ===
          String(commercial || "").toLowerCase() &&
        String(obj.indicateur || "").toLowerCase() ===
          String(indicateur || "").toLowerCase() &&
        Number(obj.annee_reference) === Number(annee) &&
        Number(obj.mois) === Number(mois)
    );
  }

  function getCANmoins1(commercial, exerciceDebut, mois) {
    const anneeNMoins1 = getAnneeDuMoisExercice(mois, exerciceDebut - 1);

    const actionsNMoins1 = toutesActions.filter((a) => {
      if (a.type !== "Payé") return false;
      if (!a.date) return false;

      const d = new Date(a.date + "T00:00:00");
      const moisAction = d.getMonth() + 1;
      const anneeAction = d.getFullYear();

      if (moisAction !== mois || anneeAction !== anneeNMoins1) {
        return false;
      }

      if (commercial === "Toute l'équipe") return true;

      const club = clubs.find(
        (c) => String(c.id || c.identifiant) === String(a.club_id)
      );

      return (
        club &&
        String(club.commercial || "").toLowerCase() ===
          String(commercial || "").toLowerCase()
      );
    });

    return actionsNMoins1.reduce(
      (sum, a) => sum + (Number(a.montantTTC) || 0),
      0
    );
  }

  function getVisitesNmoins1(commercial, exerciceDebut, mois) {
    const anneeNMoins1 = getAnneeDuMoisExercice(mois, exerciceDebut - 1);

    const visitesNMoins1 = planning.filter((a) => {
      if (!estVisiteRealisee(a)) return false;
      if (!a.date_rdv) return false;

      const d = new Date(a.date_rdv + "T00:00:00");
      const moisAction = d.getMonth() + 1;
      const anneeAction = d.getFullYear();

      if (moisAction !== mois || anneeAction !== anneeNMoins1) {
        return false;
      }

      if (commercial === "Toute l'équipe") return true;

      return normaliserCommercial(a.commercial) === normaliserCommercial(commercial);
    });

    return visitesNMoins1.length;
  }

  function calculerPourcentageProgression(objectif, nMoins1) {
    const base = Number(nMoins1) || 0;
    const cible = Number(objectif) || 0;

    if (base === 0) return "-";

    const progression = ((cible - base) / base) * 100;
    return `${progression.toFixed(1)}%`;
  }

  async function enregistrerObjectifMois(mois, indicateur, valeurObjectif) {
    const exerciceDebut = Number(objectifExerciceSelection);
    const anneeReferenceLigne = getAnneeDuMoisExercice(mois, exerciceDebut);

    const objectifExistant = getObjectifLigne(
      objectifCommercialSelection,
      exerciceDebut,
      mois,
      indicateur
    );

    if (objectifExistant) {
      const { error } = await supabase
        .from("objectifs")
        .update({
          valeur_objectif: Number(valeurObjectif) || 0,
          exercice_fiscal: exerciceDebut
        })
        .eq("id", objectifExistant.id);

      if (error) {
        console.log("ERREUR UPDATE OBJECTIF :", error);
        alert("Erreur lors de la mise à jour de l'objectif");
        return;
      }
    } else {
      const { error } = await supabase
        .from("objectifs")
        .insert([
          {
            commercial: objectifCommercialSelection,
            indicateur: indicateur,
            mois: mois,
            annee_reference: anneeReferenceLigne,
            exercice_fiscal: exerciceDebut,
            valeur_objectif: Number(valeurObjectif) || 0
          }
        ]);

      if (error) {
        console.log("ERREUR INSERT OBJECTIF :", error);
        alert("Erreur lors de l'enregistrement de l'objectif");
        return;
      }
    }

    await chargerObjectifs();
  }

  const codesObjectifs = new Set(clubs.map((club) => String(club.numero_compte_interclub || "").trim()).filter(Boolean));
  const commercialObjectif = objectifCommercialSelection === "Toute l'équipe" ? "" : objectifCommercialSelection;
  const getCAEncaisseObjectif = (annee, mois) => mouvementsPaiementInterclub(
    documentsInterclub,
    codesObjectifs,
    { commercialCommande: commercialObjectif, codesClientsConnus: codesObjectifs, inclureNonRapproches: true }
  ).filter((mouvement) => {
    const date = String(mouvement.date || "").slice(0, 10);
    return date.startsWith(`${annee}-${String(mois).padStart(2, "0")}-`);
  }).reduce((total, mouvement) => total + Number(mouvement.montantTTC || 0), 0);
  const getVisitesObjectif = (annee, mois) => planning.filter((action) => {
    const date = String(action.date_rdv || "").slice(0, 10);
    if (!estVisiteRealisee(action) || !date.startsWith(`${annee}-${String(mois).padStart(2, "0")}-`)) return false;
    if (!commercialObjectif) return true;
    return normaliserCommercial(action.commercial) === normaliserCommercial(commercialObjectif);
  }).length;
  const getTransformationObjectif = (annee, mois) => {
    const dernierJour = new Date(Number(annee), Number(mois), 0).getDate();
    const clubsRepli = commercialObjectif
      ? clubs.filter((club) => normaliserCommercial(club.commercial) === normaliserCommercial(commercialObjectif))
      : clubs;
    return syntheseTransformationDevis(documentsInterclub, clubsRepli, {
      commercial: commercialObjectif,
      debut: `${annee}-${String(mois).padStart(2, "0")}-01`,
      fin: `${annee}-${String(mois).padStart(2, "0")}-${String(dernierJour).padStart(2, "0")}`
    });
  };

  if (moisExercice.length) return <ObjectifsCommercial
    moisExercice={moisExercice}
    commercial={objectifCommercialSelection}
    setCommercial={setObjectifCommercialSelection}
    exercice={objectifExerciceSelection}
    setExercice={setObjectifExerciceSelection}
    getObjectif={(mois, indicateur) => getObjectifLigne(objectifCommercialSelection, Number(objectifExerciceSelection), mois, indicateur)}
    getCa={getCAEncaisseObjectif}
    getVisites={getVisitesObjectif}
    getTransformation={getTransformationObjectif}
    enregistrer={enregistrerObjectifMois}
    onRetour={() => setScreen("dashboard")}
  />;

  return (
    <div style={stylePage}>
      <div
        style={{
          background: "white",
          padding: 20,
          borderRadius: 12,
          marginBottom: 20
        }}
      >
        <h2>Tableau des objectifs</h2>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 15 }}>
          <select
            style={{ padding: 10, width: 220 }}
            value={objectifCommercialSelection}
            onChange={(e) => setObjectifCommercialSelection(e.target.value)}
          >
            <option>Bruno</option>
            <option>Adrien</option>
            <option>Hervé</option>
            <option>Non attribué</option>
            <option>Toute l'équipe</option>
          </select>

          <select
            style={{ padding: 10, width: 220 }}
            value={objectifExerciceSelection}
            onChange={(e) => setObjectifExerciceSelection(e.target.value)}
          >
            <option value="2024">Ex24-25</option>
            <option value="2025">Ex25-26</option>
            <option value="2026">Ex26-27</option>
            <option value="2027">Ex27-28</option>
          </select>
        </div>
      </div>

      <div
        style={{
          background: "white",
          padding: 20,
          borderRadius: 12,
          overflowX: "auto"
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f5f5f5" }}>
              <th style={{ padding: 10, border: "1px solid #ddd", textAlign: "left" }}>
                Mois
              </th>
              <th style={{ padding: 10, border: "1px solid #ddd", textAlign: "right" }}>
                CA encaissé N-1
              </th>
              <th style={{ padding: 10, border: "1px solid #ddd", textAlign: "right" }}>
                Objectif CA
              </th>
              <th style={{ padding: 10, border: "1px solid #ddd", textAlign: "right" }}>
                % prog OBJ CA
              </th>
              <th style={{ padding: 10, border: "1px solid #ddd", textAlign: "right" }}>
                Visites N-1
              </th>
              <th style={{ padding: 10, border: "1px solid #ddd", textAlign: "right" }}>
                Objectif visites
              </th>
              <th style={{ padding: 10, border: "1px solid #ddd", textAlign: "right" }}>
                % prog OBJ visites
              </th>
              <th style={{ padding: 10, border: "1px solid #ddd", textAlign: "center" }}>
                Action
              </th>
            </tr>
          </thead>

          <tbody>
            {moisExercice.map((ligne) => {
              const objectifCAExistant = getObjectifLigne(
                objectifCommercialSelection,
                Number(objectifExerciceSelection),
                ligne.mois,
                "ca_encaisse_ttc"
              );

              const objectifVisitesExistant = getObjectifLigne(
                objectifCommercialSelection,
                Number(objectifExerciceSelection),
                ligne.mois,
                "visites"
              );

              const valeurObjectifCA = objectifCAExistant
                ? Number(objectifCAExistant.valeur_objectif) || 0
                : 0;

              const valeurObjectifVisites = objectifVisitesExistant
                ? Number(objectifVisitesExistant.valeur_objectif) || 0
                : 0;

              const caNMoins1 = getCANmoins1(
                objectifCommercialSelection,
                Number(objectifExerciceSelection),
                ligne.mois
              );

              const visitesNMoins1 = getVisitesNmoins1(
                objectifCommercialSelection,
                Number(objectifExerciceSelection),
                ligne.mois
              );

              const progressionCA = calculerPourcentageProgression(
                valeurObjectifCA,
                caNMoins1
              );

              const progressionVisites = calculerPourcentageProgression(
                valeurObjectifVisites,
                visitesNMoins1
              );

              return (
                <tr
                  key={`${objectifCommercialSelection}-${objectifExerciceSelection}-${ligne.mois}`}
                >
                  <td style={{ padding: 10, border: "1px solid #ddd" }}>
                    {ligne.label}
                  </td>

                  <td
                    style={{
                      padding: 10,
                      border: "1px solid #ddd",
                      textAlign: "right"
                    }}
                  >
                    {caNMoins1} €
                  </td>

                  <td
                    style={{
                      padding: 10,
                      border: "1px solid #ddd",
                      textAlign: "right"
                    }}
                  >
                    <input
                      type="number"
                      defaultValue={valeurObjectifCA}
                      id={`objectif-ca-${objectifCommercialSelection}-${objectifExerciceSelection}-${ligne.mois}`}
                      style={{ padding: 8, width: 120, textAlign: "right" }}
                    />
                  </td>

                  <td
                    style={{
                      padding: 10,
                      border: "1px solid #ddd",
                      textAlign: "right"
                    }}
                  >
                    {progressionCA}
                  </td>

                  <td
                    style={{
                      padding: 10,
                      border: "1px solid #ddd",
                      textAlign: "right"
                    }}
                  >
                    {visitesNMoins1}
                  </td>

                  <td
                    style={{
                      padding: 10,
                      border: "1px solid #ddd",
                      textAlign: "right"
                    }}
                  >
                    <input
                      type="number"
                      defaultValue={valeurObjectifVisites}
                      id={`objectif-visites-${objectifCommercialSelection}-${objectifExerciceSelection}-${ligne.mois}`}
                      style={{ padding: 8, width: 120, textAlign: "right" }}
                    />
                  </td>

                  <td
                    style={{
                      padding: 10,
                      border: "1px solid #ddd",
                      textAlign: "right"
                    }}
                  >
                    {progressionVisites}
                  </td>

                  <td
                    style={{
                      padding: 10,
                      border: "1px solid #ddd",
                      textAlign: "center"
                    }}
                  >
                    <button
                      style={{
                        padding: 8,
                        background: "#1565c0",
                        color: "white",
                        border: "none",
                        borderRadius: 6
                      }}
                      onClick={async () => {
                        const inputCA = document.getElementById(
                          `objectif-ca-${objectifCommercialSelection}-${objectifExerciceSelection}-${ligne.mois}`
                        );
                        const inputVisites = document.getElementById(
                          `objectif-visites-${objectifCommercialSelection}-${objectifExerciceSelection}-${ligne.mois}`
                        );

                        const valeurCA = inputCA ? inputCA.value : 0;
                        const valeurVisites = inputVisites ? inputVisites.value : 0;

                        await enregistrerObjectifMois(
                          ligne.mois,
                          "ca_encaisse_ttc",
                          valeurCA
                        );

                        await enregistrerObjectifMois(
                          ligne.mois,
                          "visites",
                          valeurVisites
                        );
                      }}
                    >
                      Enregistrer
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div style={{ marginTop: 20 }}>
          <button
            style={{ padding: 10 }}
            onClick={() => setScreen("dashboard")}
          >
            Retour dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
/* =========================================================
  BLOC 17C - RENDU ECRAN IMPORT COMPTA
========================================================= */
if (userConnected && screen === "importCompta") {
 return (
  <ImportFichiers
    userConnected={userConnected}
    retourDashboard={() => setScreen("dashboard")}
  />
); 
}
/* =========================================================
  BLOC 18 - RENDU DASHBOARD PRINCIPAL
========================================================= */

if (userConnected && screen === "dashboard") {
  const ouvrirDevisDashboard = async (devis) => {
    const club = clubs.find((item) => String(item.id || item.identifiant) === String(devis.club_id));
    if (!club) return;
    await chargerDetailClub(club);
    setDevisInitialId(devis.id);
    setScreen("devisCRM");
  };
  const ouvrirContratDashboard = async (contrat) => {
    const club = clubs.find((item) => String(item.id || item.identifiant) === String(contrat.club_id));
    if (!club) return;
    await chargerDetailClub(club);
    setContratRenouvelerId(null);
    setContratInitialId(contrat.id);
    setScreen("contratsCRM");
  };
  const naviguerDashboard = (destination, options = {}) => {
    if (destination === "enlevementBoutiqueClub") {
      setBoutiqueClubClubInitial(null);
      setBoutiqueClubDestinationInitiale({ section:'commandes', etape:'4', sousEtape:'4.2' });
      setScreen("boutiqueClub");
      return;
    }
    if (destination === "boutiqueClub") { setBoutiqueClubClubInitial(null); setBoutiqueClubDestinationInitiale(null); }
    if (destination === "devisCRM" || destination === "devisCRMNouveau") setDevisInitialId(null);
    if (destination === "interclub") setCodeClientInterclubInitial(String(options.codeClient || ""));
    if (destination === "objectifs") {
      setObjectifCommercialSelection(userConnected?.nom || "Bruno");
      setObjectifExerciceSelection("2026");
    }
    setScreen(destination);
  };
  return <DashboardCrm
    logo={logo} version={VERSION} groupe={groupeSelectionne} utilisateur={userConnected}
    clientSupabase={supabaseClientActif} clubs={clubs} clubsFiltres={clubsFiltres} clubsCandidats={clubsFiltresHorsCommercial}
    documentsInterclub={documentsInterclub}
    actionsDashboard={actionsPlanningDashboard} situations={clubsFiltres.map(getSituationInterclubClub)}
    documentsDisponibles={!erreurDocumentsInterclub}
    filtreCommercial={filtreCommercial} setFiltreCommercial={setFiltreCommercial}
    filtreSport={filtreSport} setFiltreSport={setFiltreSport}
    filtreFinancier={filtreFinancier} setFiltreFinancier={setFiltreFinancier}
    filtrePeriode={filtrePeriode} setFiltrePeriode={setFiltrePeriode}
    recherche={recherche} setRecherche={setRecherche}
    exerciceSelectionne={exerciceSelectionne} setExerciceSelectionne={setExerciceSelectionne}
    anneeSelectionnee={anneeSelectionnee} moisSelectionne={moisSelectionne} setMoisSelectionne={setMoisSelectionne}
    semaineSelectionnee={semaineSelectionnee} setSemaineSelectionnee={setSemaineSelectionnee}
    dateDepuis={dateDepuis} setDateDepuis={setDateDepuis}
    anneesDashboard={anneesDashboard} moisDashboard={moisDashboard} sportsOptions={sportsOptions}
    nombreSemainesSelectionne={nombreSemainesSelectionne}
    lundiSemaineSelectionnee={lundiSemaineSelectionnee} dimancheSemaineSelectionnee={dimancheSemaineSelectionnee}
    dateDebut={formatDateFiltreDashboard(getDateDebutFiltreDashboard())}
    dateFin={formatDateFiltreDashboard(getDateFinFiltreDashboard())}
    contexteFinancier={contexteFinancier}
    objectifs={{ objectifMoisCA, realiseMoisCA, factureMoisCA, margeFactureeMois, margeEncaisseeMois, financeCaCanonique, periodeMoisCA: periodesCaMetier.mois, periodeCumulCA: periodesCaMetier.cumul, sourceFactureCA: donneesInterclubV2Disponibles ? 'Interclub V2' : 'indisponible', sourceEncaisseCA: encaissementsInterclubDisponibles ? 'Interclub V2' : 'indisponible', objectifsCaMensuels, tauxMoisCA, ecartMoisCA, statutMoisCA, objectifMoisVisites, realiseMoisVisites, tauxMoisVisites, ecartMoisVisites, statutMoisVisites, objectifCumulCA, realiseCumulCA, factureCumulCA, margeFactureeCumul, margeEncaisseeCumul, tauxCumulCA, ecartCumulCA, statutCumulCA, objectifCumulVisites, realiseCumulVisites, tauxCumulVisites, ecartCumulVisites, statutCumulVisites }}
    resumeDevis={resumeDevisDashboard} setResumeDevis={setResumeDevisDashboard}
    onChangerAnnee={(nouvelleAnnee) => {
      setAnneeSelectionnee(nouvelleAnnee);
      if (filtrePeriode === "Semaine") {
        const maximum = getNombreSemainesIso(nouvelleAnnee);
        if (Number(semaineSelectionnee) > maximum) setSemaineSelectionnee(String(maximum));
      }
    }}
    onNavigate={naviguerDashboard}
    onPlanning={async () => { await chargerPlanning(); setScreen("planning"); }}
    onNewClub={() => setScreen("club")}
    onExport={exportClubsCSV} onLogout={handleLogout}
    onClub={async (club) => { setAfficherToutesActionsClub(false); setScreenRetourClub("dashboard"); await chargerDetailClub(club); setScreen("detailClub"); }}
    onDevis={ouvrirDevisDashboard} onContrat={ouvrirContratDashboard}
    onFiltreObjectif={(bloc, periode) => { setFiltreBlocDashboard(bloc); setFiltrePeriode(periode); }}
    onResetBloc={() => setFiltreBlocDashboard("Tous")}
    filtreBlocDashboard={filtreBlocDashboard}
    peutBoutique={BOUTIQUECLUB_ENABLED && ["superviseur", "gestionnaire", "commercial"].includes(userConnected?.role)}
    estSuperviseur={userConnected?.role === "superviseur"}
    actionsPlanning={planning}
    onCreerActionsPoint={async ({ clubs: clubsCibles, type, responsable, echeance, motif, periodes, reference = "", montant = 0, typeOrigine = null, dateDocument = null, modeDate = "A_FAIRE_AVANT_LE", priorite = "Normale", commentaire = "", origine = "POINT COMMERCIAL" }) => {
      const lignes = clubsCibles.map((club) => ({
        jour: new Date(`${echeance}T12:00:00`).toLocaleDateString("fr-FR", { weekday: "long" }).replace(/^./, (c) => c.toUpperCase()),
        date_rdv: echeance, heure_rdv: "09:00", duree_rdv: "30min", type: normaliserTypeAction(type),
        commercial: responsable, club_id: club.id || club.identifiant, clubCode: club.code || "", clubNom: club.nom || "",
        commentaire: encoderCommentaireAction(commentaire || motif, { objet: type, statut: "planifie", priorite: priorite || (type === "RELANCE RENFORCÉE" ? "Haute" : "Normale"), responsable, mode_date: modeDate, origine, reference, element_lie: reference, montant: Number(montant || 0), montant_document_d2: Number(montant || 0), type_origine: typeOrigine, date_document: dateDocument, commercial_analyse: filtreCommercial === "Moi" ? userConnected?.nom : filtreCommercial, mois_analyse: `${anneeSelectionnee}-${String(moisSelectionne).padStart(2, "0")}`, periodes, createur: userConnected?.nom || "", date_creation: new Date().toISOString() }),
        groupe: groupeSelectionne
      }));
      const { data, error } = await supabaseClientActif.from("planning").insert(lignes).select();
      if (error) throw error;
      setPlanning((actuel) => [...(data || []), ...actuel]);
    }}
    onPreparerPrime={(contexte) => {
      if (!accesGestionPrimes(userConnected)) return;
      setContextePrime(contexte || contextePrime || { commercial: filtreCommercial === "Moi" ? userConnected?.nom : filtreCommercial, annee: anneeSelectionnee, mois: moisSelectionne, indicateurs: {} });
      setScreen("gestionPrimes");
    }}
  />;
}

if (userConnected) {
  const totalAdh = clubsFiltres.reduce(
    (sum, c) => sum + Number(c.adherents || 0),
    0
  );


  return (
    <div style={stylePage}>
      <div
        style={{
          background: "white",
          padding: 20,
          borderRadius: 12,
          marginBottom: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 20,
          flexWrap: "wrap",
          position: "sticky",
          top: 0,
          zIndex: 1000,
          boxShadow: "0 4px 12px rgba(0,0,0,0.12)"
        }}
      >
<div style={{ display: "flex", alignItems: "center", gap: 15, flexWrap: "wrap" }}>
  <img
    src={logo}
    alt="Logo"
    style={{
      width: 60,
      height: "auto",
      display: "block"
    }}
  />

  <div>
    <div style={{ fontSize: 12, color: "#666" }}>
      Version : {VERSION} | Groupe : {groupeSelectionne || "non défini"}
    </div>

    <h2 style={{ margin: 0 }}>CRM Clubs</h2>

    <div>
      Commercial connecté : <strong>{userConnected?.nom || ""}</strong>
    </div>
  </div>
</div>

<div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <button
            style={{ padding: 10 }}
            onClick={() => setScreen("club")}
          >
            + Nouveau club
          </button>

          <button
            style={{
              padding: "6px 10px",
              background: "#1565c0",
              color: "white",
              border: "none",
              borderRadius: 6,
              fontSize: 12
            }}
            onClick={async () => {
              await chargerPlanning();
              setScreen("planning");
            }}
          >
            📅 Planning
          </button>

          {BOUTIQUECLUB_ENABLED && ["superviseur", "commercial"].includes(userConnected?.role) && (
            <button
              style={{ padding: 10, background: "#6a1b9a", color: "white", border: "none", borderRadius: 6 }}
              onClick={() => { setBoutiqueClubClubInitial(null); setBoutiqueClubDestinationInitiale(null); setScreen("boutiqueClub"); }}
            >
              BOUTIQUECLUB
            </button>
          )}

          {BOUTIQUECLUB_ENABLED && ["superviseur", "commercial"].includes(userConnected?.role) && (
            <button
              style={{ padding: 10, background: "#075ee8", color: "white", border: "none", borderRadius: 6 }}
              onClick={() => { setBoutiqueClubClubInitial(null); setBoutiqueClubDestinationInitiale({ section:'commandes', etape:'4', sousEtape:'4.2' }); setScreen("boutiqueClub"); }}
            >
              ENLÈVEMENT BOUTIQUECLUB
            </button>
          )}

          {userConnected?.role === "superviseur" && (
            <>
              <button
                style={{ padding: 10 }}
                onClick={() => {
                  setObjectifCommercialSelection(userConnected?.nom || "Bruno");
                  setObjectifExerciceSelection("2026");
                  setScreen("objectifs");
                }}
              >
                Objectifs
              </button>
              <button
                style={{ padding: 10, background: "#263238", color: "white", border: "none", borderRadius: 6 }}
                onClick={() => setScreen("administrationGroupes")}
              >
                Administration
              </button>
            </>
          )}

          <button
            style={{ padding: 10 }}
            onClick={exportClubsCSV}
          >
            Export Excel
          </button>
		  <button
  style={{ padding: 10, background: "#6a1b9a", color: "white", border: "none", borderRadius: 6 }}
  onClick={() => setScreen("importCompta")}
>
  Import compta
</button>
<button
  style={{
    padding: 10,
    background: "#1565c0",
    color: "white",
    border: "none",
    borderRadius: 6
  }}
  onClick={() => setScreen("interclub")}
>
  Clients Interclub
</button>
<button
  style={{
    padding: 10,
    background: "#2e7d32",
    color: "white",
    border: "none",
    borderRadius: 6
  }}
  onClick={() => setScreen("syntheseInterclub")}
>
  Synthèse Interclub
</button>
          <button
            style={{
              padding: 10,
              background: "#444",
              color: "white",
              border: "none",
              borderRadius: 6
            }}
            onClick={handleLogout}
          >
            Déconnexion
          </button>

          {filtreBlocDashboard !== "Tous" && (
            <button
              style={{
                padding: 10,
                background: "#ef6c00",
                color: "white",
                border: "none",
                borderRadius: 6
              }}
              onClick={() => {
                setFiltreBlocDashboard("Tous");
              }}
            >
              RAZ filtre bloc
            </button>
          )}
        </div>
      </div>

      {supabaseClientActif && <DashboardDevis
        clientSupabase={supabaseClientActif}
        groupe={groupeSelectionne}
        clubs={clubs}
        clubsFiltres={clubsFiltres}
        dateDebut={formatDateFiltreDashboard(getDateDebutFiltreDashboard())}
        dateFin={formatDateFiltreDashboard(getDateFinFiltreDashboard())}
        periodeLibelle={filtrePeriode}
        onResume={setResumeDevisDashboard}
        onOuvrir={async (devis) => {
          const club = clubs.find((item) => String(item.id || item.identifiant) === String(devis.club_id));
          if (!club) return;
          await chargerDetailClub(club);
          setDevisInitialId(devis.id);
          setScreen("devisCRM");
        }}
      />}

      {supabaseClientActif && <DashboardContrats clientSupabase={supabaseClientActif} groupe={groupeSelectionne} clubsFiltres={clubsFiltres} dateDebut={formatDateFiltreDashboard(getDateDebutFiltreDashboard())} dateFin={formatDateFiltreDashboard(getDateFinFiltreDashboard())} periodeLibelle={filtrePeriode} onOuvrir={async (contrat)=>{const club=clubs.find(item=>String(item.id||item.identifiant)===String(contrat.club_id));if(!club)return;await chargerDetailClub(club);setContratRenouvelerId(null);setContratInitialId(contrat.id);setScreen("contratsCRM")}} onRenouveler={async (contrat)=>{const club=clubs.find(item=>String(item.id||item.identifiant)===String(contrat.club_id));if(!club)return;await chargerDetailClub(club);setContratInitialId(null);setContratRenouvelerId(contrat.id);setScreen("contratsCRM")}}/>}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "2.2fr 1fr 2.2fr 1fr",
          gap: 20,
          marginBottom: 20,
          marginTop: 10
        }}
      >

{/* ===================== CA MOIS ===================== */}
<div
  onClick={() => {
    setFiltreBlocDashboard("CA_MOIS");
    setFiltrePeriode("Mois");
  }}
  style={{
    background: "white",
    padding: 20,
    borderRadius: 12,
    cursor: "pointer"
  }}
>
  <h3>CA du mois</h3>

<div>Objectif : {formatMontant(objectifMoisCA)} €</div>
<div>Réalisé : {formatMontant(realiseMoisCA)} €</div>
<div>Écart : {formatMontant(ecartMoisCA)} €</div>
  <div>% : {tauxMoisCA.toFixed(1)}%</div>
  <div>
    Statut : <strong>{statutMoisCA}</strong>
  </div>

  {filtreBlocDashboard === "CA_MOIS" && (
    <div style={{ marginTop: 10 }}>
      <span
        style={{
          background: "#ef6c00",
          color: "white",
          padding: "6px 10px",
          borderRadius: 8,
          fontSize: 12,
          fontWeight: "bold"
        }}
      >
        ⌛ Vue filtrée
      </span>
    </div>
  )}

  <div
    style={{
      marginTop: 15,
      height: 20,
      background: "#eee",
      borderRadius: 10,
      overflow: "hidden",
      display: "flex"
    }}
  >
    <div
      style={{
        width: `${largeurVertMois}%`,
        height: "100%",
        background: statutMoisCA === "Retard" ? "#ff9800" : "#4caf50"
      }}
    />

    {tauxMoisCA > 100 && (
      <div
        style={{
          width: `${largeurBleuMois}%`,
          height: "100%",
          background: "#2196f3"
        }}
      />
    )}
  </div>

  {tauxMoisCA > 100 && (
    <div style={{ marginTop: 8, color: "#2196f3", fontWeight: "bold" }}>
      Dépassé de {(tauxMoisCA - 100).toFixed(1)}%
    </div>
  )}
</div>
{/* ===================== VISITES MOIS ===================== */}
<div
  onClick={() => {
    setFiltreBlocDashboard("VISITES_MOIS");
    setFiltrePeriode("Mois");
  }}
  style={{
    background: "white",
    padding: 20,
    borderRadius: 12,
    cursor: "pointer"
  }}
>
          <h3>Visites du mois</h3>

          <div>Objectif : {objectifMoisVisites}</div>
          <div>Réalisé : {realiseMoisVisites}</div>
          <div>Écart : {ecartMoisVisites}</div>
          <div>% : {tauxMoisVisites.toFixed(1)}%</div>
          <div>
            Statut : <strong>{statutMoisVisites}</strong>
          </div>
{filtreBlocDashboard === "VISITES_MOIS" && (
  <div style={{ marginTop: 10 }}>
    <span
      style={{
        background: "#ef6c00",
        color: "white",
        padding: "6px 10px",
        borderRadius: 8,
        fontSize: 12,
        fontWeight: "bold",
        display: "inline-block"
      }}
    >
      ⌛ Vue filtrée
    </span>
  </div>
)}
          <div
            style={{
              marginTop: 15,
              height: 20,
              background: "#eee",
              borderRadius: 10,
              overflow: "hidden",
              display: "flex"
            }}
          >
            <div
              style={{
                width: `${largeurVertMoisVisites}%`,
                height: "100%",
                background:
                  statutMoisVisites === "Retard" ? "#ff9800" : "#4caf50"
              }}
            />

            {tauxMoisVisites > 100 && (
              <div
                style={{
                  width: `${largeurBleuMoisVisites}%`,
                  height: "100%",
                  background: "#2196f3"
                }}
              />
            )}
          </div>

          {tauxMoisVisites > 100 && (
            <div style={{ marginTop: 8, color: "#2196f3", fontWeight: "bold" }}>
              Dépassé de {(tauxMoisVisites - 100).toFixed(1)}%
            </div>
          )}
        </div>

        {/* ===================== CA CUMUL ===================== */}
<div
  onClick={() => {
    setFiltreBlocDashboard("CA_CUMUL");
    setFiltrePeriode("Exercice fiscal");
  }}
  style={{
    background: "white",
    padding: 20,
    borderRadius: 12,
    cursor: "pointer"
  }}
>
          <h3>Cumul CA</h3>

<div>Objectif : {formatMontant(objectifCumulCA)} €</div>
<div>Réalisé : {formatMontant(realiseCumulCA)} €</div>
<div>Écart : {formatMontant(ecartCumulCA)} €</div>
          <div>% : {tauxCumulCA.toFixed(1)}%</div>
          <div>
            Statut : <strong>{statutCumulCA}</strong>
          </div>
{filtreBlocDashboard === "CA_CUMUL" && (
  <div style={{ marginTop: 10 }}>
    <span
      style={{
        background: "#ef6c00",
        color: "white",
        padding: "6px 10px",
        borderRadius: 8,
        fontSize: 12,
        fontWeight: "bold"
      }}
    >
      ⌛ Vue filtrée
    </span>
  </div>
)}
          <div
            style={{
              marginTop: 15,
              height: 20,
              background: "#eee",
              borderRadius: 10,
              overflow: "hidden",
              display: "flex"
            }}
          >
            <div
              style={{
                width: `${largeurVertCumul}%`,
                height: "100%",
                background: statutCumulCA === "Retard" ? "#ff9800" : "#4caf50"
              }}
            />

            {tauxCumulCA > 100 && (
              <div
                style={{
                  width: `${largeurBleuCumul}%`,
                  height: "100%",
                  background: "#2196f3"
                }}
              />
            )}
          </div>

          {tauxCumulCA > 100 && (
            <div style={{ marginTop: 8, color: "#2196f3", fontWeight: "bold" }}>
              Dépassé de {(tauxCumulCA - 100).toFixed(1)}%
            </div>
          )}
        </div>

{/* ===================== VISITES CUMUL ===================== */}
<div
  onClick={() => {
    setFiltreBlocDashboard("VISITES_CUMUL");
    setFiltrePeriode("Exercice fiscal");
  }}
  style={{
    background: "white",
    padding: 20,
    borderRadius: 12,
    cursor: "pointer"
  }}
>
  <h3>Cumul visites</h3>

  <div>Objectif : {objectifCumulVisites}</div>
  <div>Réalisé : {realiseCumulVisites}</div>
  <div>Écart : {ecartCumulVisites}</div>
  <div>% : {tauxCumulVisites.toFixed(1)}%</div>
  <div>
    Statut : <strong>{statutCumulVisites}</strong>
  </div>

  {filtreBlocDashboard === "VISITES_CUMUL" && (
    <div style={{ marginTop: 10 }}>
      <span
        style={{
          background: "#ef6c00",
          color: "white",
          padding: "6px 10px",
          borderRadius: 8,
          fontSize: 12,
          fontWeight: "bold"
        }}
      >
        ⌛ Vue filtrée
      </span>
    </div>
  )}

  <div
    style={{
      marginTop: 15,
      height: 20,
      background: "#eee",
      borderRadius: 10,
      overflow: "hidden",
      display: "flex"
    }}
  >
    <div
      style={{
        width: `${largeurVertCumulVisites}%`,
        height: "100%",
        background:
          statutCumulVisites === "Retard" ? "#ff9800" : "#4caf50"
      }}
    />

    {tauxCumulVisites > 100 && (
      <div
        style={{
          width: `${largeurBleuCumulVisites}%`,
          height: "100%",
          background: "#2196f3"
        }}
      />
    )}
  </div>

  {tauxCumulVisites > 100 && (
    <div style={{ marginTop: 8, color: "#2196f3", fontWeight: "bold" }}>
      Dépassé de {(tauxCumulVisites - 100).toFixed(1)}%
    </div>
  )}
</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10, marginBottom: 16 }}>
        {[
          ["Nombre de clubs", totalClubs],
          ["Total adhérents", totalAdh],
          ["Devis en cours", `${resumeDevisDashboard?.en_cours || 0} — ${formatMontant(resumeDevisDashboard?.montant_en_cours_ttc || 0)} € TTC`],
          ["Livraisons TTC", `${formatMontant(totalLivraisonsTTC)} €`],
          ["Montant facturé TTC", `${formatMontant(totalFacturesTTC)} €`],
          ["Montant payé TTC", `${formatMontant(totalPayesTTC)} €`],
          ["Solde restant à encaisser", `${formatMontant(soldeRestantEncaisser)} €`]
        ].map(([libelle, valeur]) => <div key={libelle} style={{ background: "white", padding: 13, borderRadius: 10 }}><div style={{ fontSize: 12, color: "#777" }}>{libelle}</div><div style={{ fontSize: 21, fontWeight: "bold", marginTop: 4 }}>{valeur}</div></div>)}
      </div>

      <div
        style={{
          background: "white",
          padding: 20,
          borderRadius: 12,
          marginBottom: 20
        }}
      >
        <h3>Filtres et recherche</h3>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <input
            style={{ padding: 10, width: 320 }}
            placeholder="Code club, nom club, ville, sport, nom contact, téléphone, mail"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
          />

          <select
            style={{ padding: 10, width: 220 }}
            value={filtreCommercial}
            onChange={(e) => setFiltreCommercial(e.target.value)}
          >
            <option>Moi</option>
            <option>Adrien</option>
            <option>Hervé</option>
            <option>Bruno</option>
            <option>Non attribué</option>
            <option value="Toute l'équipe">Tous</option>
          </select>

          <select
            style={{ padding: 10, width: 220 }}
            value={filtreSport}
            onChange={(e) => setFiltreSport(e.target.value)}
          >
            <option>Tous les sports</option>
            {sportsOptions.map((sport) => (
              <option key={sport} value={sport}>
                {sport}
              </option>
            ))}
          </select>

          <select
            style={{ padding: 10, width: 240 }}
            value={filtreFinancier}
            onChange={(e) => setFiltreFinancier(e.target.value)}
          >
            <option>Tous</option>
            <option>À facturer</option>
            <option>Facturé non payé</option>
          </select>

          <select
            style={{ padding: 10, width: 220 }}
            value={filtrePeriode}
            onChange={(e) => {
              const nouvellePeriode = e.target.value;
              setFiltrePeriode(nouvellePeriode);
              if (nouvellePeriode === "Toutes périodes") {
                setFiltreBlocDashboard("Tous");
              }
            }}
          >
            <option value="Toutes périodes">Tous</option>
            <option value="Exercice fiscal">Exercice fiscal</option>
            <option value="Année">Année</option>
            <option value="Mois">Mois</option>
            <option value="Semaine">Semaine</option>
            <option value="Depuis le">Depuis le</option>
          </select>

          {filtrePeriode === "Exercice fiscal" && (
            <label style={{ display: "grid", gap: 4, fontSize: 12 }}>
              EXERCICE FISCAL
              <select
                style={{ padding: 10, width: 220 }}
                value={exerciceSelectionne}
                onChange={(e) => setExerciceSelectionne(e.target.value)}
              >
                {anneesDashboard.map((annee) => (
                  <option key={annee} value={annee}>
                    Ex{String(annee).slice(-2)}-{String(annee + 1).slice(-2)}
                  </option>
                ))}
              </select>
            </label>
          )}

          {filtrePeriode === "Année" && (
            <label style={{ display: "grid", gap: 4, fontSize: 12 }}>
              ANNÉE
              <select
                style={{ padding: 10, width: 150 }}
                value={anneeSelectionnee}
                onChange={(e) => setAnneeSelectionnee(e.target.value)}
              >
                {anneesDashboard.map((annee) => (
                  <option key={annee} value={annee}>{annee}</option>
                ))}
              </select>
            </label>
          )}

          {filtrePeriode === "Mois" && (
            <>
              <label style={{ display: "grid", gap: 4, fontSize: 12 }}>
                MOIS
                <select
                  style={{ padding: 10, width: 220 }}
                  value={moisSelectionne}
                  onChange={(e) => setMoisSelectionne(e.target.value)}
                >
                  {moisDashboard.map((mois, index) => (
                    <option key={mois} value={index + 1}>{mois}</option>
                  ))}
                </select>
              </label>
              <label style={{ display: "grid", gap: 4, fontSize: 12 }}>
                ANNÉE
                <select
                  style={{ padding: 10, width: 150 }}
                  value={anneeSelectionnee}
                  onChange={(e) => setAnneeSelectionnee(e.target.value)}
                >
                  {anneesDashboard.map((annee) => (
                    <option key={annee} value={annee}>{annee}</option>
                  ))}
                </select>
              </label>
            </>
          )}

          {filtrePeriode === "Semaine" && (
            <>
              <label style={{ display: "grid", gap: 4, fontSize: 12 }}>
                SEMAINE
                <select
                  style={{ padding: 10, width: 180 }}
                  value={semaineSelectionnee}
                  onChange={(e) => setSemaineSelectionnee(e.target.value)}
                >
                  {Array.from(
                    { length: nombreSemainesSelectionne },
                    (_, index) => index + 1
                  ).map((semaine) => (
                    <option key={semaine} value={semaine}>
                      Semaine {semaine}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: "grid", gap: 4, fontSize: 12 }}>
                ANNÉE
                <select
                  style={{ padding: 10, width: 150 }}
                  value={anneeSelectionnee}
                  onChange={(e) => {
                    const nouvelleAnnee = e.target.value;
                    setAnneeSelectionnee(nouvelleAnnee);
                    const maximum = getNombreSemainesIso(nouvelleAnnee);
                    if (Number(semaineSelectionnee) > maximum) {
                      setSemaineSelectionnee(String(maximum));
                    }
                  }}
                >
                  {anneesDashboard.map((annee) => (
                    <option key={annee} value={annee}>{annee}</option>
                  ))}
                </select>
              </label>
              <div style={{ alignSelf: "end", padding: "10px 0", fontSize: 13 }}>
                {formatDateCourte(lundiSemaineSelectionnee)} → {formatDateCourte(dimancheSemaineSelectionnee)}
              </div>
            </>
          )}

          {filtrePeriode === "Depuis le" && (
            <label style={{ display: "grid", gap: 4, fontSize: 12 }}>
              DEPUIS LE
              <input
                type="date"
                style={{ padding: 10 }}
                value={dateDepuis}
                onChange={(e) => setDateDepuis(e.target.value)}
              />
            </label>
          )}
        </div>
      </div>

<div
  style={{
    display: "flex",
    gap: 20,
    alignItems: "flex-start",
    flexWrap: "wrap"
  }}
>
  <div
    style={{
      flex: 1,
      minWidth: 320,
      background: "white",
      padding: 20,
      borderRadius: 12
    }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 10,
        marginBottom: 15
      }}
    >
      <h3 style={{ margin: 0 }}>Liste des clubs</h3>

      <button
        style={{
          padding: 10,
          background: "#1565c0",
          color: "white",
          border: "none",
          borderRadius: 6
        }}
        onClick={async () => {
          await chargerPlanning();
          setScreen("planning");
        }}
      >
        Voir le planning
      </button>
    </div>

          {clubsFiltres.map((club, index) => {
const { totalLivre } = getTotauxClub(club);

const actionsDuClubListe = getActionsDuClub(club, actionsFiltrees);

const totalDevis = actionsDuClubListe
  .filter((a) => a.type === "Offre")
  .reduce((sum, a) => sum + (Number(a.montantTTC) || 0), 0);

const totalCommande = actionsDuClubListe
  .filter((a) => a.type === "Commande")
  .reduce((sum, a) => sum + (Number(a.montantTTC) || 0), 0);
            const situationInterclub = getSituationInterclubClub(club);
            const badgeFactureNonPaye = situationInterclub.nonPaye;

            return (
              <div
                key={club.id || index}
                style={{
                  borderBottom: "1px solid #eee",
                  padding: 12,
                  cursor: "pointer"
                }}
                onClick={async () => {
setAfficherToutesActionsClub(false);
await chargerDetailClub(club);
setScreen("detailClub");
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap"
                  }}
                >
                  <strong>{club.code}</strong>
                  <span>
                    — {club.nom} — {club.ville} — {club.sport || "Sans sport"} —{" "}
                  D{formatMontant(totalDevis)} € — C{formatMontant(totalCommande)} € — L{formatMontant(totalLivre)} € —{" "}
                  {club.commercial}  
                  </span>

                  {badgeFactureNonPaye && (
                    <span
                      style={{
                        background: "#d32f2f",
                        color: "white",
                        padding: "4px 8px",
                        borderRadius: 12,
                        fontSize: 12,
                        fontWeight: "bold"
                      }}
                    >
                      🔴 Facturé non payé
                    </span>
                  )}
                  {situationInterclub.compteRattache && !badgeFactureNonPaye && (
                    <span style={{ background: "#2e7d32", color: "white", padding: "4px 8px", borderRadius: 12, fontSize: 12, fontWeight: "bold" }}>
                      🟢 Situation normale
                    </span>
                  )}
                </div>
              </div>
            );
          })}

          {clubsFiltres.length === 0 && (
            <div style={{ padding: 12 }}>Aucun club trouvé.</div>
          )}
        </div>

      </div>
    </div>
  );

}



   /* =========================================================
     BLOC 19 - RENDU ECRAN LOGIN
========================================================= */ 
  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "#f2f2f2"
      }}
    >
      <div
        style={{
          background: "white",
          padding: 30,
          borderRadius: 10,
          width: 300,
          textAlign: "center"
        }}
      >
        <img
          src={logo}
          alt="Logo"
          style={{
            width: "33%",
            display: "block",
            margin: "0 auto 20px auto"
          }}
        />

        <h2>CRM CLUBS</h2>
<select
  value={groupeSelectionne}
  onChange={(e) => {
    setSupabaseClientActif(null);
    setGroupeActif(null);
    setGroupeSelectionne(e.target.value);
  }}
  style={{ width: "100%", padding: 10, marginTop: 10 }}
>
  {groupeSelectionne === "RECETTE" && <option value="RECETTE">RECETTE</option>}
  <option value="FORQUET">FORQUET</option>
  <option value="TEST">TEST</option>
</select>
        <input
          style={{ width: "100%", padding: 10, marginTop: 10 }}
          placeholder="Utilisateur"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />

        <input
          type="password"
          style={{ width: "100%", padding: 10, marginTop: 10 }}
          placeholder="Mot de passe"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <button
          onClick={handleLogin}
          disabled={!supabaseClientActif}
          style={{
            marginTop: 20,
            width: "100%",
            padding: 12,
            background: "#333",
            color: "white",
            border: "none",
            borderRadius: 6
          }}
        >
          SE CONNECTER
        </button>
        {loginMessage && <div role="status" style={{ marginTop: 12, color: "#b42318", fontSize: 13 }}>{loginMessage}</div>}

        <button
          type="button"
          onClick={handleMotDePasseOublie}
          disabled={!supabaseClientActif}
          style={{ marginTop: 12, border: "none", background: "transparent", color: "#1565c0", cursor: "pointer" }}
        >
          Mot de passe oublié ?
        </button>
		<div style={{ marginTop: 10, fontSize: 12, color: "#999" }}>
			Version {VERSION}
		</div>
      </div>
    </div>
  );
}
/* =========================================================
   BLOC 20 - EXPORT COMPOSANT
========================================================= */
function App() {
  return (
    <Suspense fallback={<div role="status" style={{ padding: 24 }}>Chargement de l’écran…</div>}>
      <AppContenu />
    </Suspense>
  );
}

export default App; 
